import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  BadgeDollarSign,
  BarChart3,
  Calendar,
  Check,
  ClipboardList,
  Copy,
  Eye,
  FileVideo,
  Link as LinkIcon,
  Lock,
  RefreshCw,
  Scissors,
  ShieldCheck,
  Sparkles,
  Star,
  Trash2,
  Upload,
  Users,
  Wallet,
} from "lucide-react";
import SEO, { breadcrumbSchema } from "../Components/SEO";
import TheNav from "../Components/navbar";
import Footer from "../Components/footer";
import { useAuth } from "../Context/AuthContext";
import {
  appealCampaignSubmission,
  createClippingCampaign,
  createCampaignReview,
  createCampaignFundingCheckout,
  confirmCampaignFundingCheckout,
  closeOwnedCampaign,
  deleteUnpaidCampaign,
  endSubmissionTrackingForTest,
  finalizeOwnedCampaignClosure,
  getCampaignDraftForEdit,
  getCampaignMembership,
  getCampaignResources,
  joinClippingCampaign,
  listCampaignReviews,
  listCampaignOperationsQueue,
  listSocialAccounts,
  refundUnusedCampaignBudget,
  reviewCampaignSubmission,
  reviewSocialAccountVerification,
  requestSocialAccountVerification,
  resolveCampaignSubmissionDispute,
  startCampaignPayoutOnboarding,
  submitCampaignClip,
  subscribeAllCampaigns,
  subscribeCampaign,
  subscribeCampaigns,
  subscribeCampaignSubmissions,
  subscribeJoinedCampaigns,
  subscribeOwnedCampaigns,
  subscribePayouts,
  subscribeUserMemberships,
  updateCampaignStatus,
  updateClippingCampaign,
  uploadClipSource,
  uploadCampaignBanner,
  uploadCampaignResource,
  withdrawCampaignBalance,
} from "../services/clippingCampaigns";
import {
  campaignToEditableInput,
  calculateCampaignFundingBreakdown,
  CAMPAIGN_CATEGORIES,
  CAMPAIGN_STATUSES,
  calculateClipperPayout,
  formatCurrency,
  formatDate,
  formatViews,
  humanizeStatus,
  MINIMUM_WITHDRAWAL_CENTS,
  PAYOUT_STATUSES,
  PLATFORM_PAYOUT_RULES,
  SUBMISSION_STATUSES,
  SUPPORTED_CLIP_PLATFORMS,
  SUPPORTED_STREAM_PLATFORMS,
  validateCampaignInput,
} from "../utils/clippingCampaignDomain";
import { withNextPath } from "../utils/navigation";
import { assignSafeExternalUrl, getSafeExternalUrl } from "../utils/security";

const NEON = "#84ff00";
const PURPLE = "#9146FF";
const PURPLE_DEEP = "#7a3ae7";
const BG = "#07060d";
const SURFACE = "rgba(255,255,255,0.04)";
const HAIRLINE = "rgba(255,255,255,0.08)";
const GRID_BG =
  "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)";

function PageShell({ title, description, canonicalPath, breadcrumbName, children }) {
  return (
    <>
      <SEO
        title={`${title} | Kre8`}
        description={description}
        canonicalPath={canonicalPath}
        noindex
        structuredData={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: breadcrumbName || title, path: canonicalPath },
        ])}
      />
      <TheNav />
      <main className="min-h-screen antialiased" style={{ backgroundColor: BG, color: "#e5e7eb" }}>
        {children}
      </main>
      <Footer />
    </>
  );
}

function Section({ children, className = "" }) {
  return (
    <section className={`mx-auto w-full max-w-7xl px-5 py-16 sm:px-8 sm:py-20 ${className}`}>
      {children}
    </section>
  );
}

function Hero({ eyebrow, title, copy, children }) {
  return (
    <Section className="relative overflow-hidden !pt-24 sm:!pt-28">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-80"
        style={{
          background: `radial-gradient(60% 50% at 20% 20%, ${PURPLE}33, transparent 60%), radial-gradient(50% 50% at 85% 30%, ${NEON}1a, transparent 60%), radial-gradient(40% 40% at 50% 90%, ${PURPLE_DEEP}33, transparent 60%)`,
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.06]"
        style={{
          backgroundImage: GRID_BG,
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)",
        }}
      />
      <div className="relative mx-auto max-w-4xl text-center">
        <p
          className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium"
          style={{
            borderColor: `${NEON}55`,
            color: NEON,
            background: `${NEON}10`,
            boxShadow: `0 0 24px ${NEON}22`,
          }}
        >
          <Sparkles className="h-3.5 w-3.5" />
          {eyebrow}
        </p>
        <h1 className="mt-6 text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl leading-[1.05]">
          {title}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-zinc-400 sm:text-xl">
          {copy}
        </p>
        {children && <div className="mt-10 flex flex-wrap justify-center gap-3">{children}</div>}
      </div>
    </Section>
  );
}

function CTAButton({ to, variant = "primary", children }) {
  const base =
    "group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5";
  if (variant === "primary") {
    return (
      <Link to={to} className={`${base} text-black`} style={{ background: NEON, boxShadow: `0 10px 40px ${NEON}40` }}>
        {children}
        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
      </Link>
    );
  }
  return (
    <Link
      to={to}
      className={`${base} text-white`}
      style={{
        background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_DEEP})`,
        boxShadow: `0 10px 40px ${PURPLE}55, inset 0 1px 0 rgba(255,255,255,.15)`,
      }}
    >
      {children}
    </Link>
  );
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-white/10 p-6 backdrop-blur-xl ${className}`}
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
        boxShadow:
          "0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.03)",
      }}
    >
      {children}
    </div>
  );
}

function Badge({ children, tone = "neutral" }) {
  const styles = {
    neutral: { borderColor: HAIRLINE, backgroundColor: "rgba(255,255,255,0.05)", color: "#e5e7eb" },
    live: { borderColor: `${NEON}40`, backgroundColor: `${NEON}12`, color: NEON },
    purple: { borderColor: `${PURPLE}40`, backgroundColor: `${PURPLE}15`, color: "#d8b4fe" },
    warn: { borderColor: "rgba(251,191,36,0.35)", backgroundColor: "rgba(251,191,36,0.12)", color: "#fde68a" },
  };
  return (
    <span className="inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold" style={styles[tone]}>
      {children}
    </span>
  );
}

function Metric({ label, value, icon: Icon, tone = "live" }) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-zinc-500">{label}</p>
          <p className="mt-2 text-2xl font-bold text-white">{value}</p>
        </div>
        <div
          className="grid h-11 w-11 place-items-center rounded-xl"
          style={{ backgroundColor: tone === "purple" ? `${PURPLE}18` : `${NEON}15` }}
        >
          <Icon className="h-5 w-5" style={{ color: tone === "purple" ? PURPLE : NEON }} />
        </div>
      </div>
    </Card>
  );
}

function FieldMock({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <p className="mt-2 text-sm font-medium text-zinc-100">{value}</p>
    </div>
  );
}

const inputClass =
  "mt-2 w-full rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-white outline-none transition placeholder:text-zinc-600 focus:border-emerald-300/40 focus:ring-2 focus:ring-emerald-400/10";

function FormField({ label, error, children }) {
  return (
    <label className="block text-sm font-semibold text-zinc-200">
      {label}
      {children}
      {error && <span className="mt-1.5 block text-xs font-medium text-red-300">{error}</span>}
    </label>
  );
}

function Notice({ tone = "neutral", children }) {
  const toneClass = tone === "error"
    ? "border-red-300/25 bg-red-500/10 text-red-100"
    : tone === "success"
      ? "border-emerald-300/25 bg-emerald-500/10 text-emerald-100"
      : "border-white/10 bg-white/[0.04] text-zinc-300";
  return <div className={`rounded-xl border px-4 py-3 text-sm ${toneClass}`}>{children}</div>;
}

function EmptyState({ title, copy, action }) {
  return (
    <Card className="text-center">
      <Scissors className="mx-auto h-8 w-8" style={{ color: NEON }} />
      <h3 className="mt-4 text-xl font-bold text-white">{title}</h3>
      <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-zinc-400">{copy}</p>
      {action && <div className="mt-6">{action}</div>}
    </Card>
  );
}

function LoadingState({ label = "Loading campaigns" }) {
  return (
    <div className="grid min-h-[220px] place-items-center rounded-2xl border border-white/10 bg-white/[0.03] text-sm text-zinc-400">
      <div className="text-center">
        <RefreshCw className="mx-auto h-6 w-6 animate-spin" style={{ color: NEON }} />
        <p className="mt-3">{label}</p>
      </div>
    </div>
  );
}

function RatingStars({ value }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} className={`h-4 w-4 ${star <= value ? "fill-amber-300 text-amber-300" : "text-zinc-700"}`} />
      ))}
    </span>
  );
}

function PublicReviewForm({ campaignId, currentUser, subjectId, onSaved }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      setNotice("");
      await createCampaignReview({ campaignId, user: currentUser, subjectId, rating, comment });
      setComment("");
      setNotice("Your review is now public.");
      await onSaved?.();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="flex flex-wrap gap-2" aria-label="Review rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <button key={star} type="button" onClick={() => setRating(star)} className="rounded-lg p-1" title={`${star} stars`}>
            <Star className={`h-6 w-6 ${star <= rating ? "fill-amber-300 text-amber-300" : "text-zinc-700"}`} />
          </button>
        ))}
      </div>
      <textarea className={`${inputClass} min-h-24 resize-y`} value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Share what it was like working together" />
      {error && <Notice tone="error">{error}</Notice>}
      {notice && <Notice tone="success">{notice}</Notice>}
      <button type="submit" disabled={saving} className="rounded-xl px-4 py-3 text-sm font-bold text-black disabled:opacity-50" style={{ backgroundColor: NEON }}>{saving ? "Publishing Review" : "Publish Review"}</button>
    </form>
  );
}

function ReviewsPanel({ reviews }) {
  if (reviews.length === 0) return <Notice>No public reviews yet.</Notice>;
  const average = reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-2xl font-bold text-white">{average.toFixed(1)}</span>
        <RatingStars value={Math.round(average)} />
        <span className="text-sm text-zinc-500">{reviews.length} review{reviews.length === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-3">
        {reviews.map((review) => (
          <div key={review.id || `${review.reviewerId}-${review.subjectId}`} className="border-t border-white/10 pt-4 first:border-0 first:pt-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-white">@{review.reviewerUsername}</p>
              <RatingStars value={review.rating} />
            </div>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">{review.comment}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SocialAccountVerificationPanel({ currentUser, onAccountsChange }) {
  const [accounts, setAccounts] = useState([]);
  const [platform, setPlatform] = useState("TikTok");
  const [profileUrl, setProfileUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const items = await listSocialAccounts({ user: currentUser });
      setAccounts(items);
      onAccountsChange?.(items);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [currentUser]);

  const requestVerification = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      setNotice("");
      const result = await requestSocialAccountVerification({ user: currentUser, platform, profileUrl });
      setNotice(result.status === "verified"
        ? "This account is already verified."
        : `Add ${result.verificationCode} to your profile bio, leave it there, then wait for Kre8 approval.`);
      setProfileUrl("");
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-500/10"><ShieldCheck className="h-5 w-5" style={{ color: NEON }} /></div>
        <div><h2 className="text-xl font-bold text-white">Verified posting accounts</h2><p className="mt-1 text-sm text-zinc-400">Verify each account once before using it in campaign submissions.</p></div>
      </div>
      {loading ? <div className="mt-5 text-sm text-zinc-500">Loading accounts...</div> : (
        <div className="mt-5 space-y-3">
          {accounts.map((account) => (
            <div key={account.id} className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3"><div><p className="font-semibold text-white">{account.platform} · @{account.handle}</p><a href={getSafeExternalUrl(account.profileUrl) || undefined} target="_blank" rel="noopener noreferrer" className="mt-1 block text-xs" style={{ color: NEON }}>Open profile</a></div><Badge tone={account.status === "verified" ? "live" : account.status === "rejected" ? "warn" : "purple"}>{humanizeStatus(account.status)}</Badge></div>
              {account.status === "pending" && <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.03] p-3"><p className="text-xs text-zinc-500">Temporary bio code</p><div className="mt-1 flex items-center justify-between gap-3"><code className="font-bold text-white">{account.verificationCode}</code><button type="button" title="Copy verification code" onClick={() => navigator.clipboard?.writeText(account.verificationCode)}><Copy className="h-4 w-4 text-zinc-400" /></button></div></div>}
              {account.rejectionReason && <p className="mt-3 text-sm text-amber-200">{account.rejectionReason}</p>}
            </div>
          ))}
        </div>
      )}
      <form onSubmit={requestVerification} className="mt-5 grid gap-3 sm:grid-cols-[180px_1fr_auto] sm:items-end">
        <FormField label="Platform"><select className={inputClass} value={platform} onChange={(event) => setPlatform(event.target.value)}>{SUPPORTED_CLIP_PLATFORMS.map((item) => <option key={item}>{item}</option>)}</select></FormField>
        <FormField label="Public profile link"><input className={inputClass} value={profileUrl} onChange={(event) => setProfileUrl(event.target.value)} placeholder="https://tiktok.com/@youraccount" /></FormField>
        <button type="submit" disabled={saving} className="rounded-xl px-4 py-3 text-sm font-bold text-black disabled:opacity-50" style={{ backgroundColor: NEON }}>{saving ? "Requesting" : "Verify Account"}</button>
      </form>
      {error && <div className="mt-4"><Notice tone="error">{error}</Notice></div>}
      {notice && <div className="mt-4"><Notice tone="success">{notice}</Notice></div>}
    </Card>
  );
}

function CampaignMemberVerificationQueue({ campaignId, currentUser }) {
  const [accounts, setAccounts] = useState([]);
  const [reasons, setReasons] = useState({});
  const [workingId, setWorkingId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      setAccounts(await listSocialAccounts({
        user: currentUser,
        scope: "campaign",
        campaignId,
      }));
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [campaignId, currentUser]);

  const handleReview = async (account, action) => {
    try {
      setWorkingId(account.id);
      setError("");
      await reviewSocialAccountVerification({
        user: currentUser,
        accountId: account.id,
        action,
        reason: reasons[account.id] || "",
        campaignId,
      });
      await load();
    } catch (reviewError) {
      setError(reviewError.message);
    } finally {
      setWorkingId("");
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: NEON }}>Member verification</p>
          <h2 className="mt-2 text-2xl font-bold text-white">Pending posting accounts</h2>
          <p className="mt-2 text-sm text-zinc-400">Approve only accounts belonging to people who joined this campaign.</p>
        </div>
        <Badge tone="purple">{accounts.length} pending</Badge>
      </div>
      {error && <div className="mt-5"><Notice tone="error">{error}</Notice></div>}
      <div className="mt-5 space-y-3">
        {loading ? <p className="text-sm text-zinc-500">Loading pending accounts...</p> : accounts.length === 0 ? <Notice>No campaign members are waiting for a bio check.</Notice> : accounts.map((account) => (
          <div key={account.id} className="grid gap-4 border-t border-white/10 pt-4 first:border-0 first:pt-0 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
            <div>
              <p className="font-semibold text-white">{account.platform} · @{account.handle}</p>
              <a href={getSafeExternalUrl(account.profileUrl) || undefined} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-2 text-sm" style={{ color: NEON }}>Open profile <ArrowRight className="h-4 w-4" /></a>
              <p className="mt-2 text-xs text-zinc-500">Confirm the bio contains <strong className="text-white">{account.verificationCode}</strong></p>
            </div>
            <FormField label="Rejection reason"><input className={inputClass} value={reasons[account.id] || ""} onChange={(event) => setReasons((previous) => ({ ...previous, [account.id]: event.target.value }))} placeholder="Only required when rejecting" /></FormField>
            <div className="flex gap-2">
              <button type="button" disabled={workingId === account.id} onClick={() => handleReview(account, "approve")} className="rounded-xl border border-emerald-300/25 bg-emerald-500/10 px-4 py-3 text-sm font-bold text-emerald-100 disabled:opacity-50">Approve</button>
              <button type="button" disabled={workingId === account.id} onClick={() => handleReview(account, "reject")} className="rounded-xl border border-red-300/25 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-100 disabled:opacity-50">Reject</button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PlatformBadges({ platforms = [] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {platforms.map((platform) => <Badge key={platform} tone="purple">{platform}</Badge>)}
    </div>
  );
}

function BudgetProgress({ campaign }) {
  const total = campaign.budgetCents || 0;
  const used = campaign.approvedSpendCents || 0;
  const reserved = campaign.reservedBudgetCents || 0;
  const usedPercentage = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const reservedPercentage = total > 0
    ? Math.min(100 - usedPercentage, (reserved / total) * 100)
    : 0;
  const available = Math.max(0, (campaign.budgetRemainingCents || 0) - reserved);
  return (
    <div>
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-semibold text-white">{formatCurrency(used)} used</span>
        <span className="text-zinc-500">{formatCurrency(total)}</span>
      </div>
      <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-white/10">
        <div className="h-full" style={{ width: `${usedPercentage}%`, backgroundColor: NEON }} />
        <div className="h-full" style={{ width: `${reservedPercentage}%`, backgroundColor: PURPLE }} />
      </div>
      {reserved > 0 && <p className="mt-2 text-xs text-zinc-500">{formatCurrency(reserved)} reserved for pending clips · {formatCurrency(available)} open</p>}
    </div>
  );
}

function FundingBadge({ campaign }) {
  if (campaign.fundingStatus === "funded") return <Badge tone="live">Funded</Badge>;
  if (campaign.fundingStatus === "checkout_pending") return <Badge tone="warn">Payment pending</Badge>;
  if (campaign.fundingStatus === "paid_needs_attention") return <Badge tone="warn">Payment needs attention</Badge>;
  if (campaign.fundingStatus === "refund_pending") return <Badge tone="warn">Refund pending</Badge>;
  if (campaign.fundingStatus === "unused_budget_refunded") return <Badge>Unused budget refunded</Badge>;
  return null;
}

const isStreamerAccount = (profile) => ["creator", "streamer"].includes(
  String(profile?.profileInfo?.role || "").trim().toLowerCase()
);

const CAMPAIGN_COMMITMENT_DAYS = 60;
const confirmCampaignFunding = (campaign) => {
  const breakdown = calculateCampaignFundingBreakdown(campaign?.budgetCents);
  return window.confirm(
    `Fund ${formatCurrency(breakdown.budgetCents)} for this campaign? Your total Stripe charge will be ${formatCurrency(breakdown.totalChargeCents)}. The campaign commitment lasts 60 days, and platform and processing fees are not refundable.`
  );
};

const getCampaignRefundEligibleAt = (campaign) => {
  const stored = campaign?.refundEligibleAt?.toDate instanceof Function
    ? campaign.refundEligibleAt.toDate()
    : campaign?.refundEligibleAt
      ? new Date(campaign.refundEligibleAt)
      : null;
  if (stored && Number.isFinite(stored.getTime())) return stored;
  const fundedValue = campaign?.funding?.paidAt || campaign?.publishedAt;
  const fundedAt = fundedValue?.toDate instanceof Function
    ? fundedValue.toDate()
    : fundedValue
      ? new Date(fundedValue)
      : null;
  if (!fundedAt || !Number.isFinite(fundedAt.getTime())) return null;
  return new Date(fundedAt.getTime() + CAMPAIGN_COMMITMENT_DAYS * 24 * 60 * 60 * 1000);
};

const isCampaignOpenForJoining = (campaign) => {
  if (campaign?.status !== CAMPAIGN_STATUSES.LIVE || campaign.fundingStatus !== "funded") {
    return false;
  }
  const scheduledEndAt = campaign.scheduledEndAt?.toDate instanceof Function
    ? campaign.scheduledEndAt.toDate()
    : campaign.scheduledEndAt
      ? new Date(campaign.scheduledEndAt)
      : null;
  return !scheduledEndAt || scheduledEndAt.getTime() > Date.now();
};

const isCampaignOpenForSubmission = (campaign) => {
  if (campaign?.fundingStatus !== "funded") return false;
  if (campaign.status === CAMPAIGN_STATUSES.CLOSING) {
    const graceEndsAt = campaign.submissionGraceEndsAt?.toDate instanceof Function
      ? campaign.submissionGraceEndsAt.toDate()
      : new Date(campaign.submissionGraceEndsAt || 0);
    return Number.isFinite(graceEndsAt.getTime()) && graceEndsAt.getTime() >= Date.now();
  }
  if (campaign.status !== CAMPAIGN_STATUSES.LIVE) return false;
  const scheduledEndAt = campaign.scheduledEndAt?.toDate instanceof Function
    ? campaign.scheduledEndAt.toDate()
    : campaign.scheduledEndAt
      ? new Date(campaign.scheduledEndAt)
      : null;
  if (!scheduledEndAt) return true;
  const graceEndsAt = scheduledEndAt.getTime() +
    Math.max(0, Number(campaign.submissionWindowHours) || 0) * 60 * 60 * 1000;
  return graceEndsAt >= Date.now();
};

function CampaignCard({ campaign, joined = false, managed = false, owned = false, viewerIsStreamer = false, compact = false, onFund, onDelete, funding = false, deleting = false }) {
  const statusTone = campaign.status === CAMPAIGN_STATUSES.LIVE ? "live" : "neutral";
  return (
    <Card className="h-full overflow-hidden !p-0">
      <Link to={`/clipping-campaigns/${campaign.id}`} className="block aspect-[16/8] overflow-hidden bg-black/30">
        {campaign.bannerUrl ? (
          <img src={campaign.bannerUrl} alt="" className="h-full w-full object-cover transition duration-500 hover:scale-[1.02]" />
        ) : (
          <div className="grid h-full place-items-center" style={{ background: `linear-gradient(135deg, ${PURPLE}44, ${NEON}1f)` }}>
            <Scissors className="h-10 w-10 text-white/40" />
          </div>
        )}
      </Link>
      <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap gap-2">
            <Badge tone={statusTone}>{humanizeStatus(campaign.status)}</Badge>
            <Badge>{campaign.streamPlatform || "Stream"}</Badge>
            <Badge tone="purple">{campaign.category}</Badge>
            {campaign.game && <Badge>{campaign.game}</Badge>}
            {joined && <Badge tone="live">Joined</Badge>}
            {managed && <FundingBadge campaign={campaign} />}
          </div>
          <h3 className="mt-4 text-xl font-bold text-white">{campaign.title}</h3>
          <p className="mt-1 text-sm text-zinc-400">by @{campaign.creatorUsername}</p>
        </div>
        <Scissors className="h-6 w-6" style={{ color: NEON }} />
      </div>
      <div className="mt-5"><BudgetProgress campaign={campaign} /></div>
      <div className="mt-5"><PlatformBadges platforms={campaign.targetPlatforms} /></div>
      {!compact && (
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <FieldMock label="RPM" value={`${formatCurrency(campaign.rpmCents)} per 1K`} icon={BarChart3} />
          <FieldMock label="Approval" value={campaign.approvalRate == null ? "New" : `${campaign.approvalRate}%`} icon={Check} />
          <FieldMock label="Clippers" value={campaign.memberCount || 0} icon={Users} />
        </div>
      )}
      <div className="mt-6 flex flex-wrap gap-3">
        <CTAButton to={`/clipping-campaigns/${campaign.id}`}>View Campaign</CTAButton>
        {managed ? (
          campaign.status === CAMPAIGN_STATUSES.LIVE ? (
            <CTAButton to={`/clipping-campaigns/${campaign.id}/review`} variant="secondary">Review Submissions</CTAButton>
          ) : campaign.status === CAMPAIGN_STATUSES.DRAFT ? (
            <>
              <CTAButton to={`/clipping-campaigns/${campaign.id}/edit`} variant="secondary">Edit Campaign</CTAButton>
              <button
                type="button"
                onClick={() => onFund?.(campaign)}
                disabled={funding || campaign.fundingStatus === "paid_needs_attention"}
                className="rounded-full px-7 py-3.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                style={{ background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_DEEP})` }}
              >
                {funding ? "Opening checkout" : campaign.fundingStatus === "checkout_pending" ? "Resume Payment" : campaign.fundingStatus === "paid_needs_attention" ? "Contact Support" : "Fund & Launch"}
              </button>
              {campaign.fundingStatus !== "paid_needs_attention" && <button type="button" onClick={() => onDelete?.(campaign)} disabled={deleting || funding} className="inline-flex items-center gap-2 rounded-full border border-red-300/20 bg-red-500/10 px-5 py-3.5 text-sm font-semibold text-red-100 transition hover:border-red-300/40 disabled:cursor-not-allowed disabled:opacity-50"><Trash2 className="h-4 w-4" />{deleting ? "Deleting" : "Delete Draft"}</button>}
            </>
          ) : null
        ) : owned ? (
          <CTAButton
            to={campaign.status === CAMPAIGN_STATUSES.DRAFT
              ? `/clipping-campaigns/${campaign.id}/edit`
              : `/clipping-campaigns/${campaign.id}`}
            variant="secondary"
          >
            {campaign.status === CAMPAIGN_STATUSES.DRAFT ? "Edit Campaign" : "Manage Campaign"}
          </CTAButton>
        ) : viewerIsStreamer ? (
          <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-5 py-3 text-sm font-semibold text-zinc-400">
            Streamer accounts cannot join
          </span>
        ) : (
          <CTAButton to={joined && isCampaignOpenForSubmission(campaign) ? `/clipping-campaigns/${campaign.id}/submit` : joined ? `/clipping-campaigns/${campaign.id}` : `/clipping-campaigns/${campaign.id}/join`} variant="secondary">
            {joined ? isCampaignOpenForSubmission(campaign) ? "Submit Clip" : "View History" : "Join Campaign"}
          </CTAButton>
        )}
      </div>
      </div>
    </Card>
  );
}

function SubmissionStatusCard({
  submission,
  isRunView,
  appealReason,
  appealing,
  onAppealReasonChange,
  onAppeal,
}) {
  const reviewedAt = submission.reviewedAt?.toDate instanceof Function
    ? submission.reviewedAt.toDate()
    : submission.reviewedAt
      ? new Date(submission.reviewedAt)
      : null;
  const appealIsOpen = !submission.dispute && (
    !reviewedAt || Date.now() <= reviewedAt.getTime() + 7 * 24 * 60 * 60 * 1000
  );
  const statusTone = submission.status === SUBMISSION_STATUSES.APPROVED
    ? "live"
    : [SUBMISSION_STATUSES.REJECTED, SUBMISSION_STATUSES.DISPUTED].includes(submission.status)
      ? "warn"
      : "neutral";

  return (
    <Card>
      <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-center">
        <div>
          <div className="flex flex-wrap gap-2">
            <Badge tone={statusTone}>{humanizeStatus(submission.status)}</Badge>
            <Badge tone="purple">{submission.platform}</Badge>
          </div>
          <p className="mt-3 font-semibold text-white">{submission.campaignTitle}</p>
          <p className="mt-1 text-sm text-zinc-400">
            {isRunView
              ? `Submitted by @${submission.clipperUsername}`
              : `${formatViews(submission.approvedViews || 0)} verified views · ${formatCurrency(submission.estimatedPayoutCents || 0)} estimated`}
          </p>
          {submission.reviewReason && <p className="mt-2 text-sm text-zinc-500">Decision: {submission.reviewReason}</p>}
          {submission.dispute?.reason && <p className="mt-2 text-sm text-amber-200">Appeal: {submission.dispute.reason}</p>}
          {submission.dispute?.status === "resolved" && <p className="mt-2 text-sm text-zinc-500">Appeal resolved: {submission.dispute.resolutionNote || humanizeStatus(submission.dispute.resolution)}</p>}
        </div>
        {isRunView ? (
          <CTAButton to={`/clipping-campaigns/${submission.campaignId}/review`} variant="secondary">
            {submission.status === SUBMISSION_STATUSES.DISPUTED ? "Review Appeal" : "Open Review"}
          </CTAButton>
        ) : (
          <a href={getSafeExternalUrl(submission.clipUrl) || undefined} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold" style={{ color: NEON }}>Open Clip</a>
        )}
      </div>
      {!isRunView && submission.status === SUBMISSION_STATUSES.REJECTED && (
        <div className="mt-4 border-t border-white/10 pt-4">
          {appealIsOpen ? (
            <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
              <FormField label="Appeal this decision">
                <input className={inputClass} value={appealReason || ""} onChange={(event) => onAppealReasonChange(event.target.value)} placeholder="Explain what should be reviewed again" />
              </FormField>
              <button type="button" onClick={onAppeal} disabled={appealing || String(appealReason || "").trim().length < 10} className="rounded-xl border border-amber-300/25 bg-amber-500/10 px-4 py-3 text-sm font-bold text-amber-100 disabled:opacity-50">
                {appealing ? "Sending Appeal" : "Send Appeal"}
              </button>
            </div>
          ) : <p className="text-sm text-zinc-500">{submission.dispute ? "This appeal has already been resolved." : "The seven day appeal window has ended."}</p>}
        </div>
      )}
    </Card>
  );
}

export function ClippingCampaignDiscoverPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [platform, setPlatform] = useState("All");
  const [status, setStatus] = useState("All open campaigns");
  const { currentUser, activeUser } = useAuth();
  const isCreator = isStreamerAccount(activeUser);

  useEffect(() => subscribeCampaigns({
    role: "editor",
    onData: (items) => { setCampaigns(items); setLoading(false); },
    onError: (loadError) => { setError(loadError.message); setLoading(false); },
  }), []);

  useEffect(() => subscribeUserMemberships(
    currentUser?.uid,
    setMemberships,
    (loadError) => setError(loadError.message)
  ), [currentUser?.uid]);

  const joinedIds = useMemo(() => new Set(memberships.map((item) => item.campaignId)), [memberships]);
  const ownedCampaigns = useMemo(
    () => campaigns.filter((item) => item.creatorId === currentUser?.uid),
    [campaigns, currentUser?.uid]
  );
  const editableCampaign = ownedCampaigns.find((item) => item.status === CAMPAIGN_STATUSES.DRAFT);
  const filtered = useMemo(() => campaigns.filter((item) => {
    const scheduledEndAt = item.scheduledEndAt?.toDate instanceof Function
      ? item.scheduledEndAt.toDate()
      : item.scheduledEndAt
        ? new Date(item.scheduledEndAt)
        : null;
    const isStillOpen = !scheduledEndAt || scheduledEndAt.getTime() > Date.now();
    const searchable = `${item.title} ${item.creatorUsername} ${item.game} ${item.category} ${item.streamPlatform || ""}`.toLowerCase();
    const matchesSearch = !search || searchable.includes(search.toLowerCase());
    const matchesCategory = category === "All" || item.category === category;
    const matchesPlatform = platform === "All" || item.targetPlatforms?.includes(platform);
    const isJoined = joinedIds.has(item.id);
    const matchesStatus = status === "All open campaigns"
      || (status === "Joined" && isJoined)
      || (status === "Not joined" && !isJoined);
    return isStillOpen && matchesSearch && matchesCategory && matchesPlatform && matchesStatus;
  }), [campaigns, search, category, platform, status, joinedIds]);

  return (
    <PageShell
      title="Discover Clipping Campaigns"
      description="Discover active gaming and IRL clipping campaigns for TikTok, YouTube Shorts, and Instagram Reels."
      canonicalPath="/clipping-campaigns/discover"
    >
      <Hero
        eyebrow="Campaign discovery"
        title="Find campaigns that fit the content you already know"
        copy="Search active gaming and IRL campaigns, compare RPM and approval rates, then review the full brief before joining."
      >
        {isCreator && editableCampaign ? (
          <CTAButton to={`/clipping-campaigns/${editableCampaign.id}/edit`}>Edit Campaign</CTAButton>
        ) : isCreator && ownedCampaigns.length > 0 ? (
          <CTAButton to="/clipping-campaigns/dashboard">Manage Campaigns</CTAButton>
        ) : isCreator ? (
          <CTAButton to="/clipping-campaigns/new">Create Campaign</CTAButton>
        ) : (
          <CTAButton to="/launch-clipping-campaign">Launch a Campaign</CTAButton>
        )}
        <CTAButton to="/clipping-campaigns/dashboard" variant="secondary">My Campaigns</CTAButton>
      </Hero>
      <Section className="!pt-0">
        <Card>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-[1fr_180px_220px_190px]">
            <input className={inputClass} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search campaigns, creators, or games" />
            <select className={inputClass} value={category} onChange={(event) => setCategory(event.target.value)}>
              {["All", "Gaming", "IRL"].map((item) => <option key={item}>{item}</option>)}
            </select>
            <select className={inputClass} value={platform} onChange={(event) => setPlatform(event.target.value)}>
              {["All", ...SUPPORTED_CLIP_PLATFORMS].map((item) => <option key={item}>{item}</option>)}
            </select>
            <select className={inputClass} value={status} onChange={(event) => setStatus(event.target.value)}>
              {["All open campaigns", "Joined", "Not joined"].map((item) => <option key={item}>{item}</option>)}
            </select>
          </div>
        </Card>
        {error && <div className="mt-5"><Notice tone="error">{error}</Notice></div>}
        <div className="mt-8">
          {loading ? <LoadingState /> : filtered.length === 0 ? (
            <EmptyState title="No campaigns match yet" copy="Try another search or filter. New funded campaigns will appear here when they go live." />
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              {filtered.map((item) => <CampaignCard
                key={item.id}
                campaign={item}
                joined={joinedIds.has(item.id)}
                owned={item.creatorId === currentUser?.uid}
                viewerIsStreamer={isCreator}
              />)}
            </div>
          )}
        </div>
      </Section>
    </PageShell>
  );
}

export function ClippingCampaignDashboard() {
  const { currentUser, activeUser } = useAuth();
  const role = activeUser?.profileInfo?.role || "";
  const isCreator = ["creator", "streamer"].includes(role.toLowerCase());
  const hasCampaignOperations = activeUser?.profileInfo?.campaignOperations === true || activeUser?.profileInfo?.isAdmin === true;
  const [view, setView] = useState("joined");
  const [ownedCampaigns, setOwnedCampaigns] = useState([]);
  const [joinedCampaigns, setJoinedCampaigns] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [ownedSubmissions, setOwnedSubmissions] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fundingId, setFundingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [appealingId, setAppealingId] = useState("");
  const [appealReasons, setAppealReasons] = useState({});
  const [error, setError] = useState("");

  useEffect(() => { if (isCreator) setView("run"); }, [isCreator]);
  useEffect(() => subscribeOwnedCampaigns(currentUser?.uid, (items) => { setOwnedCampaigns(items); if (isCreator) setLoading(false); }, (loadError) => { setError(loadError.message); setLoading(false); }), [currentUser?.uid, isCreator]);
  useEffect(() => subscribeUserMemberships(currentUser?.uid, setMemberships, (loadError) => setError(loadError.message)), [currentUser?.uid]);
  useEffect(() => subscribeCampaignSubmissions({ userId: currentUser?.uid, scope: "owner", onData: setOwnedSubmissions, onError: (loadError) => setError(loadError.message) }), [currentUser?.uid]);
  useEffect(() => subscribeCampaignSubmissions({ userId: currentUser?.uid, scope: "clipper", onData: setMySubmissions, onError: (loadError) => setError(loadError.message) }), [currentUser?.uid]);
  useEffect(() => subscribePayouts({ userId: currentUser?.uid, scope: "clipper", onData: setPayouts, onError: (loadError) => setError(loadError.message) }), [currentUser?.uid]);

  const joinedCampaignIds = useMemo(() => memberships.map((item) => item.campaignId), [memberships]);
  useEffect(() => subscribeJoinedCampaigns(
    joinedCampaignIds,
    (items) => { setJoinedCampaigns(items); if (!isCreator) setLoading(false); },
    (loadError) => { setError(loadError.message); setLoading(false); }
  ), [joinedCampaignIds, isCreator]);
  const isRunView = view === "run";
  const visibleCampaigns = isRunView ? ownedCampaigns : joinedCampaigns;
  const visibleSubmissions = isRunView ? ownedSubmissions : mySubmissions;
  const decisionsNeeded = ownedSubmissions.filter((item) =>
    [SUBMISSION_STATUSES.PENDING, SUBMISSION_STATUSES.DISPUTED].includes(item.status)
  ).length;
  const trackedViews = mySubmissions.reduce((sum, item) => sum + (item.approvedViews || item.currentViews || 0), 0);
  const estimatedEarnings = payouts.reduce((sum, item) => sum + (item.amountCents || 0), 0);
  const openBudget = ownedCampaigns.reduce((sum, item) => sum + (item.budgetRemainingCents || 0), 0);
  const approvedSpend = ownedCampaigns.reduce((sum, item) => sum + (item.approvedSpendCents || 0), 0);
  const editableOwnedCampaign = ownedCampaigns.find((item) => item.status === CAMPAIGN_STATUSES.DRAFT);
  const primaryOwnedCampaign = editableOwnedCampaign || ownedCampaigns[0];

  const handleFundCampaign = async (campaign) => {
    if (!confirmCampaignFunding(campaign)) return;
    try {
      setFundingId(campaign.id);
      setError("");
      const checkout = await createCampaignFundingCheckout({ campaignId: campaign.id, user: currentUser });
      assignSafeExternalUrl(checkout.url);
    } catch (fundingError) {
      setError(fundingError.message);
    } finally {
      setFundingId("");
    }
  };

  const handleDeleteCampaign = async (campaign) => {
    if (!window.confirm(`Delete “${campaign.title}”? This cannot be undone.`)) return;
    try {
      setDeletingId(campaign.id);
      setError("");
      await deleteUnpaidCampaign({ campaignId: campaign.id, user: currentUser });
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setDeletingId("");
    }
  };

  const handleAppeal = async (submission) => {
    try {
      setAppealingId(submission.id);
      setError("");
      await appealCampaignSubmission({
        campaignId: submission.campaignId,
        submissionId: submission.id,
        user: currentUser,
        reason: appealReasons[submission.id] || "",
      });
      setAppealReasons((previous) => ({ ...previous, [submission.id]: "" }));
    } catch (appealError) {
      setError(appealError.message);
    } finally {
      setAppealingId("");
    }
  };

  return (
    <PageShell
      title="Clipping Campaign Dashboard"
      description="Private dashboard for clipping campaigns, submissions, reviews, and earnings."
      canonicalPath="/clipping-campaigns/dashboard"
    >
      <Hero
        eyebrow="Campaign dashboard"
        title={isCreator ? "Run your clipping campaigns from one place" : "Track every campaign you join from one place"}
        copy={isCreator ? "Keep campaign drafts, funding, submitted clips, and review decisions together." : "Keep joined campaigns, submitted clips, review decisions, and estimated earnings together."}
      >
        {isCreator && primaryOwnedCampaign ? <CTAButton to={editableOwnedCampaign ? `/clipping-campaigns/${editableOwnedCampaign.id}/edit` : `/clipping-campaigns/${primaryOwnedCampaign.id}`}>{editableOwnedCampaign ? "Edit Campaign" : "Manage Campaign"}</CTAButton> : isCreator ? <CTAButton to="/clipping-campaigns/new">Create Campaign</CTAButton> : null}
        {hasCampaignOperations && <CTAButton to="/clipping-campaigns/admin">Campaign Operations</CTAButton>}
        {!isCreator && <CTAButton to="/clipping-campaigns/payouts" variant="secondary">Campaign Earnings</CTAButton>}
        <CTAButton to="/clipping-campaigns/discover" variant="secondary">Discover Campaigns</CTAButton>
      </Hero>
      <Section className="!pt-0">
        {!currentUser ? (
          <EmptyState title="Sign in to open your campaign workspace" copy="Your joined campaigns, drafts, submissions, and earnings estimates are tied to your Kre8 account." action={<CTAButton to={withNextPath("/login", "/clipping-campaigns/dashboard")}>Sign In</CTAButton>} />
        ) : <>
        {error && <div className="mb-5"><Notice tone="error">{error}</Notice></div>}
        <div className="mb-6 inline-flex rounded-xl border border-white/10 bg-white/[0.03] p-1">
          {isCreator ? <button type="button" onClick={() => setView("run")} className="rounded-lg bg-white/10 px-4 py-2 text-sm font-semibold text-white">Campaigns I Run</button> : <button type="button" onClick={() => setView("joined")} className="rounded-lg bg-white/10 px-4 py-2 text-sm font-semibold text-white">Campaigns I Joined</button>}
        </div>
        {!isCreator && !isRunView && <div className="mb-6"><SocialAccountVerificationPanel currentUser={currentUser} /></div>}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isRunView ? <>
            <Metric label="Campaigns" value={ownedCampaigns.length} icon={Scissors} />
            <Metric label="Open budget" value={formatCurrency(openBudget)} icon={Wallet} tone="purple" />
            <Metric label="Estimated spend" value={formatCurrency(approvedSpend)} icon={BadgeDollarSign} />
            <Metric label="Decisions needed" value={decisionsNeeded} icon={ClipboardList} tone="purple" />
          </> : <>
            <Metric label="Joined campaigns" value={memberships.length} icon={Scissors} />
            <Metric label="Tracked views" value={formatViews(trackedViews)} icon={Eye} tone="purple" />
            <Metric label="Estimated earnings" value={formatCurrency(estimatedEarnings)} icon={BadgeDollarSign} />
            <Metric label="Submitted clips" value={mySubmissions.length} icon={ClipboardList} tone="purple" />
          </>}
        </div>
        <div className="mt-8">
          {loading ? <LoadingState /> : visibleCampaigns.length === 0 ? (
            <EmptyState
              title={isRunView ? "No campaign drafts yet" : "No joined campaigns yet"}
              copy={isRunView ? "Creator accounts can build a draft, fund its budget, and launch it when payment succeeds." : "Open discovery, choose a live campaign, and join it to keep it in your workspace."}
              action={isRunView && isCreator ? <CTAButton to="/clipping-campaigns/new">Create Draft</CTAButton> : <CTAButton to="/clipping-campaigns/discover">Discover Campaigns</CTAButton>}
            />
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              {visibleCampaigns.map((item) => <CampaignCard key={item.id} campaign={item} joined={!isRunView} managed={isRunView} onFund={handleFundCampaign} onDelete={handleDeleteCampaign} funding={fundingId === item.id} deleting={deletingId === item.id} />)}
            </div>
          )}
        </div>
        {visibleSubmissions.length > 0 && <div className="mt-12">
          <div className="flex flex-wrap items-end justify-between gap-3"><div><p className="text-xs font-semibold uppercase tracking-wider" style={{ color: NEON }}>{isRunView ? "Review queue" : "My clips"}</p><h2 className="mt-2 text-2xl font-bold text-white">{isRunView ? "Recent submissions" : "Submission status"}</h2></div>{isRunView && <Badge tone="purple">{decisionsNeeded} need a decision</Badge>}</div>
          <div className="mt-5 space-y-3">{visibleSubmissions.slice(0, 12).map((submission) => <SubmissionStatusCard key={submission.id} submission={submission} isRunView={isRunView} appealReason={appealReasons[submission.id]} appealing={appealingId === submission.id} onAppealReasonChange={(value) => setAppealReasons((previous) => ({ ...previous, [submission.id]: value }))} onAppeal={() => handleAppeal(submission)} />)}</div>
        </div>}
        </>}
      </Section>
    </PageShell>
  );
}

export function CampaignDetailPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { currentUser, activeUser } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [membership, setMembership] = useState(null);
  const [resources, setResources] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [funding, setFunding] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [closing, setClosing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [error, setError] = useState("");
  const confirmationStarted = useRef(false);

  useEffect(() => subscribeCampaign(
    campaignId,
    (item) => { setCampaign(item); setLoading(false); },
    (loadError) => { setError(loadError.message); setLoading(false); }
  ), [campaignId]);

  const loadReviews = async () => {
    try {
      setReviews(await listCampaignReviews({ campaignId }));
    } catch (loadError) {
      setError(loadError.message);
    }
  };
  useEffect(() => { loadReviews(); }, [campaignId]);

  useEffect(() => {
    if (!currentUser || !campaign) return undefined;
    let active = true;
    getCampaignMembership(campaignId, currentUser.uid)
      .then((item) => { if (active) setMembership(item); })
      .catch((loadError) => { if (active) setError(loadError.message); });
    return () => { active = false; };
  }, [campaign, campaignId, currentUser]);

  const isOwner = currentUser?.uid === campaign?.creatorId;
  const viewerIsStreamer = isStreamerAccount(activeUser);
  const canOpenResources = Boolean(membership || isOwner);
  const canSubmitToCampaign = membership && isCampaignOpenForSubmission(campaign);
  const fundingBreakdown = calculateCampaignFundingBreakdown(campaign?.budgetCents);
  const checkoutParams = new URLSearchParams(window.location.search);
  const returnedFromCheckout = checkoutParams.get("funding") === "success";
  const returnedSessionId = checkoutParams.get("session_id");
  const sessionToConfirm = returnedSessionId || campaign?.checkoutSessionId;

  useEffect(() => {
    if (
      !sessionToConfirm ||
      !currentUser ||
      !campaign ||
      currentUser.uid !== campaign.creatorId ||
      campaign.fundingStatus !== "checkout_pending" ||
      confirmationStarted.current
    ) return;

    confirmationStarted.current = true;
    setConfirmingPayment(true);
    setError("");
    confirmCampaignFundingCheckout({
      campaignId,
      sessionId: sessionToConfirm,
      user: currentUser,
    })
      .catch((confirmationError) => setError(confirmationError.message))
      .finally(() => setConfirmingPayment(false));
  }, [campaign, campaignId, currentUser, sessionToConfirm]);

  const handleFundCampaign = async () => {
    if (!confirmCampaignFunding(campaign)) return;
    try {
      setFunding(true);
      setError("");
      const checkout = await createCampaignFundingCheckout({ campaignId, user: currentUser });
      assignSafeExternalUrl(checkout.url);
    } catch (fundingError) {
      setError(fundingError.message);
      setFunding(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!window.confirm(`Delete “${campaign.title}”? This cannot be undone.`)) return;
    try {
      setDeleting(true);
      setError("");
      await deleteUnpaidCampaign({ campaignId, user: currentUser });
      navigate("/clipping-campaigns/dashboard");
    } catch (deleteError) {
      setError(deleteError.message);
      setDeleting(false);
    }
  };

  const handleCloseCampaign = async () => {
    if (!window.confirm("End this campaign? New members will stop immediately. Clips posted before closure keep their original submission window.")) return;
    try {
      setClosing(true);
      setError("");
      await closeOwnedCampaign({ campaignId, user: currentUser });
    } catch (closeError) {
      setError(closeError.message);
    } finally {
      setClosing(false);
    }
  };

  const handleRefundCampaign = async () => {
    if (!window.confirm("Refund the unused campaign budget? Platform and processing fees are not refundable.")) return;
    try {
      setRefunding(true);
      setError("");
      await refundUnusedCampaignBudget({ campaignId, user: currentUser });
    } catch (refundError) {
      setError(refundError.message);
    } finally {
      setRefunding(false);
    }
  };

  const handleFinalizeCampaign = async () => {
    try {
      setFinalizing(true);
      setError("");
      await finalizeOwnedCampaignClosure({ campaignId, user: currentUser });
    } catch (finalizeError) {
      setError(finalizeError.message);
    } finally {
      setFinalizing(false);
    }
  };

  const openResources = async () => {
    try {
      setError("");
      const items = await getCampaignResources({ campaignId, userId: currentUser?.uid, creatorId: campaign.creatorId });
      setResources(items);
    } catch (loadError) {
      setError(loadError.message);
    }
  };

  if (loading) return <PageShell title="Campaign Detail" description="Loading campaign." canonicalPath={`/clipping-campaigns/${campaignId}`}><Section><LoadingState label="Loading campaign" /></Section></PageShell>;
  if (!campaign) return <PageShell title="Campaign Not Found" description="Campaign not found." canonicalPath={`/clipping-campaigns/${campaignId}`}><Section><EmptyState title="Campaign not found" copy="This campaign may have been removed or is not available." action={<CTAButton to="/clipping-campaigns/discover">Back to Discovery</CTAButton>} /></Section></PageShell>;

  const refundEligibleAt = getCampaignRefundEligibleAt(campaign);
  const hasUnusedBudget = Number(campaign.budgetRemainingCents || 0) > 0;
  const commitmentActive = hasUnusedBudget && (
    !refundEligibleAt || refundEligibleAt.getTime() > Date.now()
  );

  return (
    <PageShell
      title="Campaign Detail"
      description="Detailed clipping campaign brief with RPM, tracking windows, platform rules, and submission actions."
      canonicalPath={`/clipping-campaigns/${campaignId || "sample-campaign"}`}
    >
      <Hero
        eyebrow={`${humanizeStatus(campaign.status)} campaign`}
        title={campaign.title}
        copy={campaign.description}
      >
        {isOwner ? <>
          {campaign.status === CAMPAIGN_STATUSES.DRAFT ? <>
            <CTAButton to={`/clipping-campaigns/${campaignId}/edit`}>Edit Campaign</CTAButton>
            <button type="button" onClick={handleFundCampaign} disabled={funding || deleting || campaign.fundingStatus === "paid_needs_attention"} className="rounded-full px-7 py-3.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50" style={{ background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_DEEP})` }}>{funding ? "Opening checkout" : campaign.fundingStatus === "checkout_pending" ? "Resume Payment" : campaign.fundingStatus === "paid_needs_attention" ? "Contact Support" : "Fund & Launch"}</button>
            {campaign.fundingStatus !== "paid_needs_attention" && <button type="button" onClick={handleDeleteCampaign} disabled={deleting || funding} className="inline-flex items-center gap-2 rounded-full border border-red-300/20 bg-red-500/10 px-5 py-3.5 text-sm font-semibold text-red-100 transition hover:border-red-300/40 disabled:opacity-50"><Trash2 className="h-4 w-4" />{deleting ? "Deleting" : "Delete Draft"}</button>}
          </> : <CTAButton to={`/clipping-campaigns/${campaignId}/review`}>Review Submissions</CTAButton>}
          {[CAMPAIGN_STATUSES.LIVE, CAMPAIGN_STATUSES.PAUSED].includes(campaign.status) && <button type="button" onClick={handleCloseCampaign} disabled={closing || commitmentActive} className="rounded-full border border-red-300/20 bg-red-500/10 px-6 py-3.5 text-sm font-semibold text-red-100 transition hover:border-red-300/40 disabled:cursor-not-allowed disabled:opacity-50">{closing ? "Closing Campaign" : commitmentActive && refundEligibleAt ? `Close available ${formatDate(refundEligibleAt)}` : "Close Campaign"}</button>}
          {campaign.status === CAMPAIGN_STATUSES.CLOSING && <button type="button" onClick={handleFinalizeCampaign} disabled={finalizing} className="rounded-full border border-amber-300/20 bg-amber-500/10 px-6 py-3.5 text-sm font-semibold text-amber-100 transition hover:border-amber-300/40 disabled:opacity-50">{finalizing ? "Checking Campaign" : "Finalize Closure"}</button>}
          {campaign.status === CAMPAIGN_STATUSES.CLOSED && campaign.fundingStatus === "funded" && <button type="button" onClick={handleRefundCampaign} disabled={refunding || commitmentActive} className="rounded-full px-7 py-3.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50" style={{ background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_DEEP})` }}>{refunding ? "Starting Refund" : commitmentActive && refundEligibleAt ? `Refund available ${formatDate(refundEligibleAt)}` : "Refund Unused Budget"}</button>}
        </> : membership ? <CTAButton to={canSubmitToCampaign ? `/clipping-campaigns/${campaignId}/submit` : "/clipping-campaigns/dashboard"}>{canSubmitToCampaign ? "Submit Clip" : "View Campaign History"}</CTAButton> : viewerIsStreamer ? <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-5 py-3.5 text-sm font-semibold text-zinc-400">Streamer accounts cannot join</span> : isCampaignOpenForJoining(campaign) ? <CTAButton to={`/clipping-campaigns/${campaignId}/join`}>Join Campaign</CTAButton> : <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-5 py-3.5 text-sm font-semibold text-zinc-400">Campaign closed</span>}
        <CTAButton to="/clipping-campaigns/discover" variant="secondary">Back to Discovery</CTAButton>
      </Hero>
      <Section className="!pt-0">
        {error && <div className="mb-5"><Notice tone="error">{error}</Notice></div>}
        {returnedFromCheckout && campaign.fundingStatus !== "funded" && campaign.status !== CAMPAIGN_STATUSES.LIVE && <div className="mb-5"><Notice>{confirmingPayment ? "Confirming your Stripe payment now." : "Stripe confirmation is still pending. Refresh this page or check the error above."}</Notice></div>}
        {campaign.fundingStatus === "paid_needs_attention" && <div className="mb-5"><Notice tone="error">Your payment succeeded, but the campaign could not launch automatically. The Kre8 team needs to review it before anything changes.</Notice></div>}
        {isOwner && commitmentActive && refundEligibleAt && <div className="mb-5"><Notice>Unused campaign funds are committed for 60 days after payment. You can close this campaign and request its unused budget beginning {formatDate(refundEligibleAt)}. A fully spent campaign may close sooner.</Notice></div>}
        {campaign.status === CAMPAIGN_STATUSES.CLOSING && <div className="mb-5"><Notice>New members cannot join. Clips posted before closure may still be submitted until {formatDate(campaign.submissionGraceEndsAt)}. Finalize after that deadline and after every submission is reviewed.</Notice></div>}
        {[CAMPAIGN_STATUSES.CLOSED, CAMPAIGN_STATUSES.COMPLETED].includes(campaign.status) && <div className="mb-5"><Notice>This campaign is closed and kept here for its creator and participants. New campaign activity cannot begin, but its review and payout history remains available.</Notice></div>}
        {campaign.fundingStatus === "refund_pending" && <div className="mb-5"><Notice>Stripe is processing the unused budget refund. The campaign will show as completed after Stripe confirms it.</Notice></div>}
        {campaign.unusedBudgetRefund?.status === "failed" && <div className="mb-5"><Notice tone="error">The unused budget refund failed. No budget was removed, and the refund can be attempted again.</Notice></div>}
        <div className="mb-8 overflow-hidden rounded-2xl border border-white/10 bg-black/30">
          {campaign.bannerUrl && <img src={campaign.bannerUrl} alt="" className="aspect-[16/7] w-full object-cover" />}
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric label="RPM" value={formatCurrency(campaign.rpmCents)} icon={BarChart3} />
          <Metric label="Budget used" value={`${formatCurrency(campaign.approvedSpendCents)} / ${formatCurrency(campaign.budgetCents)}`} icon={Wallet} tone="purple" />
          <Metric label="Approval rate" value={campaign.approvalRate == null ? "New" : `${campaign.approvalRate}%`} icon={Check} />
          <Metric label="Tracking cap" value={`${campaign.trackingWindowDays} days`} icon={Calendar} tone="purple" />
        </div>
        <div className="mt-8 grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
          <Card>
            <div className="flex flex-wrap gap-2">
              <Badge tone={campaign.status === CAMPAIGN_STATUSES.LIVE ? "live" : "neutral"}>{humanizeStatus(campaign.status)}</Badge>
              <FundingBadge campaign={campaign} />
              <Badge tone="purple">RPM payout</Badge>
              <Badge>{campaign.streamPlatform || "Stream source"}</Badge>
              <Badge>{campaign.category}{campaign.game ? ` · ${campaign.game}` : ""}</Badge>
            </div>
            <h2 className="mt-5 text-2xl font-bold text-white">Campaign brief</h2>
            <div className="mt-5 grid gap-3">
              <FieldMock label="Stream platform" value={campaign.streamPlatform || "Not specified"} icon={FileVideo} />
              <FieldMock label="VOD links" value={`${campaign.vodLinks?.length || 0} source link${campaign.vodLinks?.length === 1 ? "" : "s"}`} icon={LinkIcon} />
              <FieldMock label="Moments to watch" value={campaign.momentsToWatch || "Use your judgment and find the strongest moments."} icon={FileVideo} />
              <FieldMock label="Style notes" value={campaign.styleNotes || "Follow the campaign brief and platform guidelines."} icon={ClipboardList} />
              <FieldMock label="Target platforms" value={campaign.targetPlatforms?.join(", ")} icon={Upload} />
              <FieldMock label="Duplicate platform rule" value={campaign.platformPayoutRule === PLATFORM_PAYOUT_RULES.EACH_PLATFORM ? "Pay each platform separately" : "Pay only the highest viewed platform"} icon={RefreshCw} />
              <FieldMock label={campaign.platformPayoutRule === PLATFORM_PAYOUT_RULES.EACH_PLATFORM ? "Maximum payout per platform post" : "Maximum payout per edit"} value={formatCurrency(campaign.maxPayoutPerSubmissionCents || Math.min(campaign.budgetCents || 0, 5000))} icon={Wallet} />
            </div>
          </Card>
          <Card>
            <h2 className="text-2xl font-bold text-white">Campaign resources</h2>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              Resource names are public. Files and external links are available after joining the campaign.
            </p>
            <div className="mt-5 space-y-3">
              {(campaign.resourceNames || []).length === 0 ? <Notice>No resources added yet.</Notice> : (campaign.resourceNames || []).map((name) => (
                <div key={name} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-zinc-200">
                  <span>{name}</span><Lock className="h-4 w-4 text-zinc-500" />
                </div>
              ))}
            </div>
            {canOpenResources && resources.length === 0 && <button type="button" onClick={openResources} className="mt-5 w-full rounded-xl px-4 py-3 text-sm font-bold text-black" style={{ backgroundColor: NEON }}>Open Resource Links</button>}
            {resources.map((resource) => (
              <a key={resource.url} href={getSafeExternalUrl(resource.url) || undefined} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center justify-between rounded-xl border border-emerald-300/20 bg-emerald-500/10 p-4 text-sm font-semibold text-emerald-100">
                {resource.name}<ArrowRight className="h-4 w-4" />
              </a>
            ))}
            {!currentUser && <div className="mt-5"><Notice>Sign in and join to open resource links.</Notice></div>}
            {activeUser && !canOpenResources && <div className="mt-5"><Notice>{viewerIsStreamer ? "Streamer accounts cannot join this campaign or open its member resources." : "Join this campaign to open resource links."}</Notice></div>}
            <div className="mt-6"><BudgetProgress campaign={campaign} /></div>
            {isOwner && campaign.status === CAMPAIGN_STATUSES.DRAFT && <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-4"><p className="text-sm font-semibold text-white">Funding summary</p><div className="mt-3 space-y-2 text-sm text-zinc-400"><div className="flex justify-between gap-4"><span>Campaign budget</span><span className="text-white">{formatCurrency(fundingBreakdown.budgetCents)}</span></div><div className="flex justify-between gap-4"><span>Kre8 platform fee (10%)</span><span className="text-white">{formatCurrency(fundingBreakdown.platformFeeCents)}</span></div><div className="flex justify-between gap-4"><span>Standard card processing</span><span className="text-white">{formatCurrency(fundingBreakdown.processingFeeCents)}</span></div><div className="flex justify-between gap-4 border-t border-white/10 pt-2 font-semibold"><span>Total due</span><span className="text-white">{formatCurrency(fundingBreakdown.totalChargeCents)}</span></div></div><p className="mt-3 text-xs leading-relaxed text-zinc-500">Funding begins a 60 day campaign commitment. Platform and processing fees are not refundable. After the commitment ends and campaign obligations are resolved, any unused campaign budget can be returned.</p></div>}
            <div className="mt-5 flex flex-wrap gap-2">
              <Badge>{campaign.memberCount || 0} joined</Badge>
              <Badge>{campaign.submissionCount || 0} submissions</Badge>
            </div>
          </Card>
        </div>
        <div className="mt-8 grid gap-5 lg:grid-cols-[1fr_0.8fr]">
          <Card><h2 className="text-2xl font-bold text-white">Public reviews</h2><div className="mt-5"><ReviewsPanel reviews={reviews} /></div></Card>
          <Card>
            <h2 className="text-2xl font-bold text-white">Review this campaign</h2>
            <p className="mt-2 text-sm text-zinc-400">Reviews publish immediately after at least one of your submissions receives a decision.</p>
            <div className="mt-5">{membership && currentUser ? <PublicReviewForm campaignId={campaignId} currentUser={currentUser} onSaved={loadReviews} /> : <Notice>Join and complete a reviewed submission to leave feedback.</Notice>}</div>
          </Card>
        </div>
      </Section>
    </PageShell>
  );
}

export function CampaignJoinPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { currentUser, activeUser } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [membership, setMembership] = useState(null);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");
  const isOwner = currentUser?.uid === campaign?.creatorId;
  const viewerIsStreamer = isStreamerAccount(activeUser);
  const campaignIsOpen = isCampaignOpenForJoining(campaign);

  useEffect(() => subscribeCampaign(campaignId, (item) => { setCampaign(item); setLoading(false); }, (loadError) => { setError(loadError.message); setLoading(false); }), [campaignId]);
  useEffect(() => {
    if (!currentUser) return undefined;
    let active = true;
    getCampaignMembership(campaignId, currentUser.uid)
      .then((item) => { if (active) setMembership(item); })
      .catch((loadError) => { if (active) setError(loadError.message); });
    return () => { active = false; };
  }, [campaignId, currentUser]);

  const handleJoin = async () => {
    try {
      setJoining(true); setError("");
      await joinClippingCampaign({ campaignId, user: currentUser, profile: activeUser, rulesAccepted });
      setMembership({ status: "active" });
      navigate(`/clipping-campaigns/${campaignId}/submit`);
    } catch (joinError) { setError(joinError.message); } finally { setJoining(false); }
  };

  return (
    <PageShell title="Join Clipping Campaign" description="Review the campaign manager and join the clipping campaign." canonicalPath={`/clipping-campaigns/${campaignId}/join`}>
      <Hero eyebrow={isOwner ? "Your campaign" : "Join campaign"} title={isOwner ? "Manage your clipping campaign" : campaign?.creatorUsername ? `Clip for @${campaign.creatorUsername}` : "Review the campaign before joining"} copy={isOwner ? "Open the campaign workspace to update its draft or manage submissions." : "Check the campaign rules, approval history, and remaining budget before you join."} />
      <Section className="!pt-0">
        {loading ? <LoadingState label="Loading campaign manager" /> : !campaign ? <EmptyState title="Campaign not found" copy="This campaign is no longer available." /> : (
          <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr]">
            <Card>
              <div className="flex items-center gap-4">
                {campaign.creatorProfilePicUrl ? <img src={campaign.creatorProfilePicUrl} alt="" className="h-16 w-16 rounded-full object-cover" /> : <div className="grid h-16 w-16 place-items-center rounded-full bg-white/10 text-xl font-bold">{campaign.creatorUsername?.[0]?.toUpperCase()}</div>}
                <div><h2 className="text-xl font-bold text-white">@{campaign.creatorUsername}</h2><p className="mt-1 text-sm text-zinc-400">Campaign manager</p></div>
              </div>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <FieldMock label="Campaign approval" value={campaign.approvalRate == null ? "New campaign" : `${campaign.approvalRate}%`} icon={Check} />
                <FieldMock label="Tracking window" value={`${campaign.trackingWindowDays} days`} icon={Calendar} />
              </div>
              <div className="mt-5"><BudgetProgress campaign={campaign} /></div>
              {error && <div className="mt-5"><Notice tone="error">{error}</Notice></div>}
              {isOwner ? <div className="mt-6"><Notice>This campaign belongs to your streamer account.</Notice><div className="mt-4"><CTAButton to={campaign.status === CAMPAIGN_STATUSES.DRAFT ? `/clipping-campaigns/${campaignId}/edit` : `/clipping-campaigns/${campaignId}`}>{campaign.status === CAMPAIGN_STATUSES.DRAFT ? "Edit Campaign" : "Manage Campaign"}</CTAButton></div></div> : viewerIsStreamer ? <div className="mt-6"><Notice>Streamer accounts cannot join clipping campaigns.</Notice></div> : !currentUser ? <div className="mt-6"><CTAButton to={withNextPath("/login", `/clipping-campaigns/${campaignId}/join`)}>Sign In to Join</CTAButton></div> : membership ? <div className="mt-6"><Notice tone="success">You already joined this campaign.</Notice><div className="mt-4"><CTAButton to={isCampaignOpenForSubmission(campaign) ? `/clipping-campaigns/${campaignId}/submit` : `/clipping-campaigns/${campaignId}`}>{isCampaignOpenForSubmission(campaign) ? "Submit a Clip" : "View Campaign History"}</CTAButton></div></div> : (
                <><label className="mt-6 flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-zinc-300"><input type="checkbox" className="mt-1" checked={rulesAccepted} onChange={(event) => setRulesAccepted(event.target.checked)} /><span>I accept the campaign RPM, tracking window, platform payout rule, and content requirements.</span></label><button type="button" onClick={handleJoin} disabled={joining || !rulesAccepted || !campaignIsOpen} className="mt-4 w-full rounded-xl px-5 py-3.5 text-sm font-bold text-black transition disabled:cursor-not-allowed disabled:opacity-50" style={{ backgroundColor: NEON }}>
                  {joining ? "Joining" : campaignIsOpen ? "Join for free" : "Campaign is closed"}
                </button></>
              )}
            </Card>
            <Card>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: NEON }}>Before you join</p>
              <h2 className="mt-2 text-2xl font-bold text-white">Know how campaign tracking works</h2>
              <div className="mt-6 space-y-3 text-sm leading-relaxed text-zinc-400">
                <p>Post only original edits made for this campaign.</p>
                <p>Submit the public link within {campaign.submissionWindowHours} hours of posting.</p>
                <p>The campaign manager manually verifies final views and approves or rejects each submission.</p>
                <p>{campaign.platformPayoutRule === PLATFORM_PAYOUT_RULES.EACH_PLATFORM ? "Each approved platform post" : "Each submitted edit"} can earn up to {formatCurrency(campaign.maxPayoutPerSubmissionCents || Math.min(campaign.budgetCents || 0, 5000))}.</p>
                <p>Approved earnings become available through your connected Stripe account.</p>
              </div>
              <div className="mt-6"><CTAButton to={`/creator/${campaign.creatorId}`} variant="secondary">View Creator Profile</CTAButton></div>
            </Card>
          </div>
        )}
      </Section>
    </PageShell>
  );
}

export function CampaignCreationFlow() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { currentUser, activeUser, authLoading } = useAuth();
  const isEditing = Boolean(campaignId);
  const [form, setForm] = useState({
    title: "", description: "", category: "Gaming", streamPlatform: "Twitch", game: "", vodLinks: "",
    momentsToWatch: "", styleNotes: "", targetPlatforms: [...SUPPORTED_CLIP_PLATFORMS],
    budget: "", rpm: "", submissionWindowHours: "48", trackingWindowDays: "30",
    campaignDurationDays: "60", maxPayoutPerSubmission: "50",
    maxSubmissionsPerClipper: "8", platformPayoutRule: PLATFORM_PAYOUT_RULES.HIGHEST_PLATFORM,
    resources: [{ name: "", url: "" }], bannerUrl: "",
  });
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(isEditing);
  const [draft, setDraft] = useState(null);

  useEffect(() => () => {
    if (bannerPreview.startsWith("blob:")) URL.revokeObjectURL(bannerPreview);
  }, [bannerPreview]);

  useEffect(() => {
    if (!isEditing || authLoading) return undefined;
    if (!currentUser) {
      setLoadingDraft(false);
      return undefined;
    }

    let active = true;
    setLoadingDraft(true);
    getCampaignDraftForEdit({ campaignId, user: currentUser })
      .then(({ campaign, resources }) => {
        if (!active) return;
        setDraft(campaign);
        setForm(campaignToEditableInput(campaign, resources));
        setBannerPreview(campaign.bannerUrl || "");
        setError("");
      })
      .catch((loadError) => {
        if (active) setError(loadError.message);
      })
      .finally(() => {
        if (active) setLoadingDraft(false);
      });

    return () => { active = false; };
  }, [authLoading, campaignId, currentUser, isEditing]);

  const update = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setFieldErrors((previous) => ({ ...previous, [key]: "" }));
  };

  const togglePlatform = (platform) => update(
    "targetPlatforms",
    form.targetPlatforms.includes(platform)
      ? form.targetPlatforms.filter((item) => item !== platform)
      : [...form.targetPlatforms, platform]
  );

  const updateResource = (index, key, value) => update("resources", form.resources.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item));
  const patchResource = (index, patch) => {
    setForm((previous) => ({
      ...previous,
      resources: previous.resources.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      ),
    }));
  };
  const removeResource = (index) => update(
    "resources",
    form.resources.length === 1
      ? [{ name: "", url: "" }]
      : form.resources.filter((_, itemIndex) => itemIndex !== index)
  );

  const handleBanner = (file) => {
    if (!file) return;
    if (bannerPreview.startsWith("blob:")) URL.revokeObjectURL(bannerPreview);
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
    setFieldErrors((previous) => ({ ...previous, bannerUrl: "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formPath = isEditing ? `/clipping-campaigns/${campaignId}/edit` : "/clipping-campaigns/new";
    if (!currentUser) { navigate(withNextPath("/login", formPath)); return; }
    try {
      setSaving(true); setError(""); setFieldErrors({});
      const resourcesForValidation = form.resources.map((resource, index) =>
        resource.file
          ? { ...resource, url: `https://pending-upload.invalid/resource-${index + 1}` }
          : resource
      );
      const formValidation = validateCampaignInput({
        ...form,
        resources: resourcesForValidation,
        bannerUrl: bannerFile ? "pending-upload" : form.bannerUrl,
      });
      if (!formValidation.isValid) {
        const validationError = new Error("Check the highlighted campaign fields.");
        validationError.fieldErrors = formValidation.errors;
        throw validationError;
      }
      let bannerUrl = form.bannerUrl;
      if (bannerFile) bannerUrl = await uploadCampaignBanner({ user: currentUser, file: bannerFile, onProgress: setUploadProgress });
      const resources = await Promise.all(form.resources.map(async (resource, index) => {
        if (!resource.file) return { name: resource.name, url: resource.url };
        const url = await uploadCampaignResource({
          user: currentUser,
          file: resource.file,
          onProgress: (progress) => patchResource(index, { uploadProgress: progress }),
        });
        return { name: resource.name || resource.file.name, url };
      }));
      const savedCampaignId = isEditing
        ? await updateClippingCampaign({ campaignId, user: currentUser, input: { ...form, resources, bannerUrl } })
        : await createClippingCampaign({ user: currentUser, profile: activeUser, input: { ...form, resources, bannerUrl } });
      navigate(`/clipping-campaigns/${savedCampaignId}`);
    } catch (saveError) {
      setError(saveError.message);
      setFieldErrors(saveError.fieldErrors || {});
    } finally { setSaving(false); }
  };
  const isCreator = isStreamerAccount(activeUser);
  return (
    <PageShell
      title={isEditing ? "Edit Clipping Campaign Draft" : "Create Clipping Campaign"}
      description={isEditing ? "Edit every field in a private clipping campaign draft." : "Create a private clipping campaign draft with content resources, RPM, budget, and tracking rules."}
      canonicalPath={isEditing ? `/clipping-campaigns/${campaignId}/edit` : "/clipping-campaigns/new"}
    >
      <Hero
        eyebrow="Campaign builder"
        title={isEditing ? "Edit your campaign draft" : "Build a campaign draft and fund it when ready"}
        copy={isEditing ? "Update any part of the brief before payment. Your changes stay private until you fund and launch the campaign." : "Create and manage the campaign from your normal creator account. A complete campaign goes live after Stripe confirms payment."}
      />
      <Section className="!pt-0">
        {authLoading || loadingDraft ? <LoadingState label={isEditing ? "Loading campaign draft" : "Loading campaign builder"} /> : !currentUser ? <EmptyState title="Sign in as a creator to build a campaign" copy="Campaign drafts are saved to your creator account." action={<CTAButton to={withNextPath("/login", isEditing ? `/clipping-campaigns/${campaignId}/edit` : "/clipping-campaigns/new")}>Sign In</CTAButton>} /> : !isCreator ? <EmptyState title="Streamer account required" copy="Streamer accounts create and manage campaigns. Non-streamer accounts can join live campaigns and submit edits." /> : isEditing && !draft ? <EmptyState title="Draft unavailable" copy={error || "This campaign cannot be edited."} action={<CTAButton to="/clipping-campaigns/dashboard">Back to Dashboard</CTAButton>} /> : (
          <form onSubmit={handleSubmit} className="grid gap-6 lg:grid-cols-[1fr_0.75fr]">
            <div className="space-y-6">
              <Card>
                <h2 className="text-2xl font-bold text-white">Campaign basics</h2>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <FormField label="Campaign title" error={fieldErrors.title}><input className={inputClass} value={form.title} onChange={(event) => update("title", event.target.value)} placeholder="August stream clip push" /></FormField>
                  <FormField label="Category" error={fieldErrors.category}><select className={inputClass} value={form.category} onChange={(event) => update("category", event.target.value)}>{CAMPAIGN_CATEGORIES.map((item) => <option key={item}>{item}</option>)}</select></FormField>
                  <FormField label="Stream platform" error={fieldErrors.streamPlatform}><select className={inputClass} value={form.streamPlatform} onChange={(event) => update("streamPlatform", event.target.value)}>{SUPPORTED_STREAM_PLATFORMS.map((item) => <option key={item}>{item}</option>)}</select></FormField>
                  {form.category === "Gaming" && <FormField label="Game" error={fieldErrors.game}><input className={inputClass} value={form.game} onChange={(event) => update("game", event.target.value)} placeholder="Fortnite" /></FormField>}
                  <div className="sm:col-span-2"><FormField label="Campaign description" error={fieldErrors.description}><textarea className={`${inputClass} min-h-28 resize-y`} value={form.description} onChange={(event) => update("description", event.target.value)} placeholder="Tell clippers what this campaign is trying to achieve." /></FormField></div>
                  <div className="sm:col-span-2"><FormField label="Campaign banner" error={fieldErrors.bannerUrl}><label className="mt-2 grid min-h-48 cursor-pointer place-items-center overflow-hidden rounded-xl border border-dashed border-white/15 bg-black/25 text-center transition hover:border-white/30">{bannerPreview ? <img src={bannerPreview} alt="Campaign banner preview" className="h-full max-h-72 w-full object-cover" /> : <span className="px-6 py-10 text-sm text-zinc-400"><Upload className="mx-auto mb-3 h-6 w-6" />Upload JPG, PNG, or WebP up to 8 MB</span>}<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event) => handleBanner(event.target.files?.[0])} /></label></FormField></div>
                </div>
              </Card>
              <Card>
                <h2 className="text-2xl font-bold text-white">Content and resources</h2>
                <div className="mt-6 space-y-5">
                  <FormField label="Twitch or Kick VOD and source links" error={fieldErrors.vodLinks}><textarea className={`${inputClass} min-h-24 resize-y`} value={form.vodLinks} onChange={(event) => update("vodLinks", event.target.value)} placeholder="One link per line" /></FormField>
                  <FormField label="Moments to watch"><textarea className={`${inputClass} min-h-24 resize-y`} value={form.momentsToWatch} onChange={(event) => update("momentsToWatch", event.target.value)} placeholder="Ranked clutch, chat reaction, funny intro fail" /></FormField>
                  <FormField label="Style notes"><textarea className={`${inputClass} min-h-24 resize-y`} value={form.styleNotes} onChange={(event) => update("styleNotes", event.target.value)} placeholder="Hook, captions, music, facecam, and brand rules" /></FormField>
                  <div>
                    <p className="text-sm font-semibold text-zinc-200">Campaign resources</p>
                    <p className="mt-1 text-xs text-zinc-500">Upload a file directly or add an external link. Resources are revealed after joining.</p>
                    <div className="mt-3 divide-y divide-white/10">
                      {form.resources.map((resource, index) => (
                        <div key={index} className="grid gap-3 py-4 first:pt-0 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                          <input className={inputClass} value={resource.name} onChange={(event) => updateResource(index, "name", event.target.value)} placeholder="Brand assets" />
                          <input className={inputClass} value={resource.url} disabled={Boolean(resource.file)} onChange={(event) => updateResource(index, "url", event.target.value)} placeholder="External link" />
                          <button type="button" onClick={() => removeResource(index)} className="grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white/[0.04] text-zinc-400 transition hover:border-red-300/30 hover:text-red-200" aria-label={`Remove resource ${index + 1}`} title="Remove resource"><Trash2 className="h-4 w-4" /></button>
                          <label className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 text-sm font-semibold text-zinc-200 transition hover:border-white/25 sm:col-span-2">
                            <Upload className="h-4 w-4" />
                            {resource.file ? resource.file.name : "Upload resource file"}
                            <input type="file" className="hidden" onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (file) patchResource(index, { file, name: resource.name || file.name, url: "", uploadProgress: 0 });
                            }} />
                          </label>
                          {resource.file && <button type="button" onClick={() => patchResource(index, { file: null, uploadProgress: 0 })} className="h-11 text-sm font-semibold text-zinc-400 transition hover:text-white">Use a link</button>}
                          {saving && resource.file && resource.uploadProgress > 0 && <p className="text-xs text-zinc-500 sm:col-span-3">Uploading {resource.name}: {resource.uploadProgress}%</p>}
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() => update("resources", [...form.resources, { name: "", url: "" }])} className="mt-3 text-sm font-semibold" style={{ color: NEON }}>Add another resource</button>
                  </div>
                </div>
              </Card>
            </div>
            <div className="space-y-6">
              <Card>
                <h2 className="text-2xl font-bold text-white">Platforms and payout rules</h2>
                <div className="mt-5 space-y-4">
                  <FormField label="Allowed platforms" error={fieldErrors.targetPlatforms}><div className="mt-2 space-y-2">{SUPPORTED_CLIP_PLATFORMS.map((platform) => <label key={platform} className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm font-medium"><input type="checkbox" checked={form.targetPlatforms.includes(platform)} onChange={() => togglePlatform(platform)} />{platform}</label>)}</div></FormField>
                  <div className="grid gap-4 sm:grid-cols-2"><FormField label="Budget in USD" error={fieldErrors.budget}><input type="number" min="10" step="1" placeholder="100" className={inputClass} value={form.budget} onChange={(event) => update("budget", event.target.value)} /></FormField><FormField label="RPM in USD" error={fieldErrors.rpm}><input type="number" min="0.01" step="0.01" placeholder="2.50" className={inputClass} value={form.rpm} onChange={(event) => update("rpm", event.target.value)} /></FormField></div>
                  <FormField label={form.platformPayoutRule === PLATFORM_PAYOUT_RULES.EACH_PLATFORM ? "Maximum payout per platform post" : "Most a clipper can earn from one "} error={fieldErrors.maxPayoutPerSubmission}><input type="number" min="1" step="1" className={inputClass} value={form.maxPayoutPerSubmission} onChange={(event) => update("maxPayoutPerSubmission", event.target.value)} /></FormField>
                  <FormField label="Link submission window" error={fieldErrors.submissionWindowHours}><select className={inputClass} value={form.submissionWindowHours} onChange={(event) => update("submissionWindowHours", event.target.value)}><option value="24">24 hours after posting</option><option value="48">48 hours after posting</option><option value="72">72 hours after posting</option><option value="168">7 days after posting</option></select></FormField>
                  <FormField label="View tracking window" error={fieldErrors.trackingWindowDays}><select className={inputClass} value={form.trackingWindowDays} onChange={(event) => update("trackingWindowDays", event.target.value)}><option value="7">7 days</option><option value="14">14 days</option><option value="30">30 days</option></select></FormField>
                  <FormField label="Campaign duration" error={fieldErrors.campaignDurationDays}><select className={inputClass} value={form.campaignDurationDays} onChange={(event) => update("campaignDurationDays", event.target.value)}><option value="60">60 days</option></select></FormField>
                  <FormField label="Maximum submissions per clipper" error={fieldErrors.maxSubmissionsPerClipper}><input type="number" min="1" max="50" className={inputClass} value={form.maxSubmissionsPerClipper} onChange={(event) => update("maxSubmissionsPerClipper", event.target.value)} /></FormField>
                  <FormField label="Same edit posted across platforms"><select className={inputClass} value={form.platformPayoutRule} onChange={(event) => update("platformPayoutRule", event.target.value)}><option value={PLATFORM_PAYOUT_RULES.HIGHEST_PLATFORM}>Pay only the highest viewed platform</option><option value={PLATFORM_PAYOUT_RULES.EACH_PLATFORM}>Pay each platform separately</option></select></FormField>
                </div>
              </Card>
              <Notice><strong>Draft only.</strong> Saving does not collect money or publish the campaign. When the brief is ready, use Fund & Launch from the campaign dashboard. After Stripe confirms payment, the campaign runs for at least 60 days. Platform and processing fees are nonrefundable, and unused campaign funds become refundable after the commitment ends.</Notice>
              {error && <Notice tone="error">{error}</Notice>}
              {saving && bannerFile && <Notice>Uploading banner: {uploadProgress}%</Notice>}
              <button type="submit" disabled={saving} className="w-full rounded-xl px-5 py-4 text-sm font-bold text-black transition disabled:cursor-not-allowed disabled:opacity-50" style={{ backgroundColor: NEON }}>{saving ? "Saving Campaign Draft" : isEditing ? "Save Draft Changes" : "Save Campaign Draft"}</button>
            </div>
          </form>
        )}
      </Section>
    </PageShell>
  );
}

export function ClipSubmissionFlow() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [membership, setMembership] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    sourceVideoUrl: "",
    sourceVideoName: "",
    sourceTimestamp: "",
    posts: SUPPORTED_CLIP_PLATFORMS.map((platform, index) => ({
      platform, enabled: index === 0, clipUrl: "", postedAt: "", caption: "",
    })),
    originalWorkAccepted: false,
    rulesAccepted: false,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => subscribeCampaign(campaignId, setCampaign, (loadError) => setError(loadError.message)), [campaignId]);
  useEffect(() => {
    if (!currentUser) {
      setMembershipLoading(false);
      return undefined;
    }
    let active = true;
    setMembershipLoading(true);
    getCampaignMembership(campaignId, currentUser.uid)
      .then((item) => { if (active) setMembership(item); })
      .catch((loadError) => { if (active) setError(loadError.message); })
      .finally(() => { if (active) setMembershipLoading(false); });
    return () => { active = false; };
  }, [campaignId, currentUser]);

  const update = (key, value) => { setForm((previous) => ({ ...previous, [key]: value })); setFieldErrors((previous) => ({ ...previous, [key]: "" })); };
  const updatePost = (platform, key, value) => {
    setForm((previous) => ({
      ...previous,
      posts: previous.posts.map((post) => post.platform === platform ? { ...post, [key]: value } : post),
    }));
    setFieldErrors((previous) => ({ ...previous, posts: "" }));
  };
  const verifiedPlatforms = useMemo(
    () => new Set(accounts.filter((account) => account.status === "verified").map((account) => account.platform)),
    [accounts]
  );
  useEffect(() => {
    const allowed = new Set(campaign?.targetPlatforms || []);
    setForm((previous) => ({
      ...previous,
      posts: previous.posts.map((post) => ({
        ...post,
        enabled: post.enabled && allowed.has(post.platform) && verifiedPlatforms.has(post.platform),
      })),
    }));
  }, [campaign?.targetPlatforms, verifiedPlatforms]);

  const handleSourceUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      setError("");
      const uploaded = await uploadClipSource({ user: currentUser, file, onProgress: setUploadProgress });
      update("sourceVideoUrl", uploaded.url);
      update("sourceVideoName", uploaded.name);
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setUploading(false);
    }
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true); setError(""); setFieldErrors({});
      await submitCampaignClip({ campaignId, user: currentUser, input: form });
      navigate("/clipping-campaigns/dashboard");
    } catch (saveError) { setError(saveError.message); setFieldErrors(saveError.fieldErrors || {}); } finally { setSaving(false); }
  };
  const campaignAcceptsSubmissions = [
    CAMPAIGN_STATUSES.LIVE,
    CAMPAIGN_STATUSES.CLOSING,
  ].includes(campaign?.status);

  return (
    <PageShell
      title="Submit Clip"
      description="Submit a public short form clip link to a joined clipping campaign."
      canonicalPath={`/clipping-campaigns/${campaignId}/submit`}
    >
      <Hero
        eyebrow="Submit clip"
        title="Post fast, then submit the link back to the campaign"
        copy="Clippers should submit the campaign link quickly after posting so view tracking can start inside the campaign window."
      >
        <CTAButton to="/clipping-campaigns/dashboard">Back to Dashboard</CTAButton>
      </Hero>
      <Section className="!pt-0">
        {!currentUser ? <EmptyState title="Sign in to submit a clip" copy="Clip submissions must be connected to a Kre8 account." action={<CTAButton to={withNextPath("/login", `/clipping-campaigns/${campaignId}/submit`)}>Sign In</CTAButton>} /> : membershipLoading || !campaign ? <LoadingState label="Loading campaign" /> : !membership ? <EmptyState title="Join this campaign first" copy="Review the campaign manager and rules before submitting your first clip." action={<CTAButton to={`/clipping-campaigns/${campaignId}/join`}>Review and Join</CTAButton>} /> : (
        <div className="space-y-5">
          {campaign.status === CAMPAIGN_STATUSES.CLOSING && (
            <Notice>
              This campaign is closing. You can submit clips posted before the campaign started
              closing until {formatDate(campaign.submissionGraceEndsAt)}.
            </Notice>
          )}
          <SocialAccountVerificationPanel currentUser={currentUser} onAccountsChange={setAccounts} />
          <div className="grid gap-5 lg:grid-cols-[1fr_0.72fr]">
          <Card>
            <h2 className="text-2xl font-bold text-white">Submit one edit across platforms</h2>
            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              <FormField label="Source edit" error={fieldErrors.sourceVideo}>
                <input type="file" accept="video/*" className={`${inputClass} file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white`} onChange={handleSourceUpload} />
              </FormField>
              {uploading && <Notice>Uploading {uploadProgress}%</Notice>}
              {form.sourceVideoUrl && <Notice tone="success">Uploaded {form.sourceVideoName}</Notice>}
              <FormField label="Source VOD timestamp"><input className={inputClass} value={form.sourceTimestamp} onChange={(event) => update("sourceTimestamp", event.target.value)} placeholder="01:14:22" /></FormField>
              <div className="space-y-3">
                {(campaign.targetPlatforms || SUPPORTED_CLIP_PLATFORMS).map((platform) => {
                  const post = form.posts.find((item) => item.platform === platform);
                  const verified = verifiedPlatforms.has(platform);
                  return <div key={platform} className="rounded-xl border border-white/10 bg-black/20 p-4"><div className="flex items-center justify-between gap-3"><label className="flex items-center gap-3 text-sm font-semibold text-white"><input type="checkbox" checked={post?.enabled || false} disabled={!verified} onChange={(event) => updatePost(platform, "enabled", event.target.checked)} />{platform}</label><Badge tone={verified ? "live" : "warn"}>{verified ? "Verified" : "Verification required"}</Badge></div>{post?.enabled && <div className="mt-4 grid gap-3 sm:grid-cols-2"><FormField label="Public post link"><input className={inputClass} value={post.clipUrl} onChange={(event) => updatePost(platform, "clipUrl", event.target.value)} placeholder="Paste the posted clip link" /></FormField><FormField label="Posted date and time"><input type="datetime-local" className={inputClass} value={post.postedAt} onChange={(event) => updatePost(platform, "postedAt", event.target.value)} /></FormField><div className="sm:col-span-2"><FormField label="Caption"><textarea className={`${inputClass} min-h-20 resize-y`} value={post.caption} onChange={(event) => updatePost(platform, "caption", event.target.value)} placeholder="Caption used on this platform" /></FormField></div></div>}</div>;
                })}
              </div>
              {fieldErrors.posts && <p className="text-xs text-red-300">{fieldErrors.posts}</p>}
              <label className="flex items-start gap-3 text-sm text-zinc-300"><input type="checkbox" className="mt-1" checked={form.originalWorkAccepted} onChange={(event) => update("originalWorkAccepted", event.target.checked)} /><span>I created this edit and have the right to submit it.</span></label>{fieldErrors.originalWorkAccepted && <p className="text-xs text-red-300">{fieldErrors.originalWorkAccepted}</p>}
              <label className="flex items-start gap-3 text-sm text-zinc-300"><input type="checkbox" className="mt-1" checked={form.rulesAccepted} onChange={(event) => update("rulesAccepted", event.target.checked)} /><span>I accept the campaign RPM, tracking window, content rules, and platform payout rule.</span></label>{fieldErrors.rulesAccepted && <p className="text-xs text-red-300">{fieldErrors.rulesAccepted}</p>}
              {error && <Notice tone="error">{error}</Notice>}
              <button type="submit" disabled={saving || uploading || !campaignAcceptsSubmissions} className="w-full rounded-xl px-5 py-3.5 text-sm font-bold text-black disabled:cursor-not-allowed disabled:opacity-50" style={{ backgroundColor: NEON }}>{saving ? "Submitting Edit" : campaignAcceptsSubmissions ? "Submit Edit for Tracking" : "Campaign is not accepting submissions"}</button>
            </form>
          </Card>
          <Card>
            <h2 className="text-2xl font-bold text-white">Submission reminders</h2>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-zinc-400">
              <p>Submit fast. Most viral growth happens in the first 24 to 48 hours if the hook catches.</p>
              <p>Many campaigns check initial views around the 7 day mark.</p>
              <p>The campaign stops counting views after its selected tracking cap.</p>
              <p>Posting multiple clips gives you more chances to build total tracked views.</p>
            </div>
            <div className="mt-5 space-y-2">
              <Badge tone="live">Submit within {campaign.submissionWindowHours} hours</Badge>
              <Badge tone="purple">Tracking stops after {campaign.trackingWindowDays} days</Badge>
              <Badge>Up to {formatCurrency(campaign.maxPayoutPerSubmissionCents || Math.min(campaign.budgetCents || 0, 5000))} per {campaign.platformPayoutRule === PLATFORM_PAYOUT_RULES.EACH_PLATFORM ? "platform post" : "edit"}</Badge>
            </div>
            <div className="mt-5"><Notice>A 5% clipper service fee is deducted from approved earnings. Withdrawals unlock once your available balance reaches $10.</Notice></div>
          </Card>
          </div>
        </div>
        )}
      </Section>
    </PageShell>
  );
}

export function CampaignReviewPage() {
  const { campaignId } = useParams();
  const { currentUser, activeUser } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [inputs, setInputs] = useState({});
  const [workingId, setWorkingId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => subscribeCampaign(campaignId, setCampaign, (loadError) => setError(loadError.message)), [campaignId]);
  useEffect(() => subscribeCampaignSubmissions({ campaignId, userId: currentUser?.uid, scope: "owner", onData: setSubmissions, onError: (loadError) => setError(loadError.message) }), [campaignId, currentUser?.uid]);

  const updateInput = (submissionId, key, value) => setInputs((previous) => ({ ...previous, [submissionId]: { ...previous[submissionId], [key]: value } }));
  const handleReview = async (submission, action) => {
    try {
      setWorkingId(submission.id); setError("");
      const reviewInput = { campaignId, submissionId: submission.id, user: currentUser, action, views: inputs[submission.id]?.views, reason: inputs[submission.id]?.reason };
      const reviewDueAt = new Date(submission.reviewDueAt?.toDate?.() || submission.reviewDueAt).getTime();
      const operatorCanTakeOver = submission.status === SUBMISSION_STATUSES.PENDING && Number.isFinite(reviewDueAt) && reviewDueAt <= Date.now();
      if (!isOwner && hasCampaignOperations && (submission.status === SUBMISSION_STATUSES.DISPUTED || operatorCanTakeOver)) {
        await resolveCampaignSubmissionDispute(reviewInput);
      } else {
        await reviewCampaignSubmission(reviewInput);
      }
    } catch (reviewError) { setError(reviewError.message); } finally { setWorkingId(""); }
  };
  const handleEndTrackingForTest = async (submission) => {
    const confirmed = window.confirm(
      "End this submission's tracking window now? This is a test override and will be recorded."
    );
    if (!confirmed) return;
    try {
      setWorkingId(submission.id); setError("");
      await endSubmissionTrackingForTest({ campaignId, submissionId: submission.id, user: currentUser });
    } catch (trackingError) { setError(trackingError.message); } finally { setWorkingId(""); }
  };

  const isOwner = currentUser?.uid === campaign?.creatorId;
  const hasCampaignOperations = activeUser?.profileInfo?.campaignOperations === true || activeUser?.profileInfo?.isAdmin === true;
  const canOpenReviewQueue = isOwner || hasCampaignOperations;
  return (
    <PageShell
      title="Review Campaign Submissions"
      description="Creator review queue for approving, rejecting, revising, and disputing campaign submissions."
      canonicalPath={`/clipping-campaigns/${campaignId}/review`}
    >
      <Hero
        eyebrow="Creator review"
        title="Approve verified views or reject with a reason"
        copy="The campaign manager checks the public post, enters the verified view count, and approves or rejects the submission."
      >
        <CTAButton to="/clipping-campaigns/dashboard">Back to Dashboard</CTAButton>
      </Hero>
      <Section className="!pt-0">
        {error && <div className="mb-5"><Notice tone="error">{error}</Notice></div>}
        {currentUser && campaign && isOwner && <div className="mb-5"><CampaignMemberVerificationQueue campaignId={campaignId} currentUser={currentUser} /></div>}
        {campaign && canOpenReviewQueue && !isOwner && <div className="mb-5"><Notice>Campaign operations access is active. You can resolve appeals and take over submissions after the creator review deadline passes.</Notice></div>}
        {!currentUser ? <EmptyState title="Sign in to review submissions" copy="Only the campaign manager can open this review queue." /> : campaign && !canOpenReviewQueue ? <EmptyState title="Campaign manager access only" copy="Only the creator who owns this campaign can approve views or change submission status." /> : !campaign ? <LoadingState label="Loading campaign submissions" /> : submissions.length === 0 ? <EmptyState title="No submissions yet" copy="Joined clippers will appear here after they post and submit their links." /> : (
        <div className="space-y-4">
          {submissions.map((submission) => {
            const trackingIsOpen = new Date(submission.trackingEndsAt?.toDate?.() || submission.trackingEndsAt).getTime() > Date.now();
            const reviewDueAt = new Date(submission.reviewDueAt?.toDate?.() || submission.reviewDueAt).getTime();
            const reviewIsOverdue = submission.status === SUBMISSION_STATUSES.PENDING && !trackingIsOpen && Number.isFinite(reviewDueAt) && reviewDueAt <= Date.now();
            const payoutIsLocked = [PAYOUT_STATUSES.PROCESSING, PAYOUT_STATUSES.PAID].includes(submission.payoutStatus);
            const canDecide = !payoutIsLocked && (isOwner || (hasCampaignOperations && (submission.status === SUBMISSION_STATUSES.DISPUTED || reviewIsOverdue)));
            return (
            <Card key={submission.id}>
              <div className="grid gap-5 lg:grid-cols-[1fr_0.9fr] lg:items-center">
                <div>
                  <div className="flex flex-wrap gap-2">
                    <Badge tone={submission.status === SUBMISSION_STATUSES.APPROVED ? "live" : submission.status === SUBMISSION_STATUSES.REVISION ? "warn" : "neutral"}>
                      {humanizeStatus(submission.status)}
                    </Badge>
                    <Badge tone="purple">{submission.platform}</Badge>
                    {reviewIsOverdue && <Badge tone="warn">Review overdue</Badge>}
                  </div>
                  <h3 className="mt-4 text-xl font-bold text-white">@{submission.clipperUsername}</h3>
                  <p className="mt-2 text-sm text-zinc-400">
                    Source timestamp {submission.sourceTimestamp || "not provided"}. Posted {formatDate(submission.postedAt)} at {formatCurrency(campaign.rpmCents)} RPM.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-4"><a href={getSafeExternalUrl(submission.clipUrl) || undefined} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: NEON }}>Open posted clip <ArrowRight className="h-4 w-4" /></a>{submission.verifiedSocialProfileUrl && <a href={getSafeExternalUrl(submission.verifiedSocialProfileUrl) || undefined} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-purple-200"><ShieldCheck className="h-4 w-4" />Verified @{submission.verifiedSocialHandle}</a>}{submission.sourceVideoUrl && <a href={getSafeExternalUrl(submission.sourceVideoUrl) || undefined} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-300"><FileVideo className="h-4 w-4" />Raw video</a>}</div>
                  <div className="mt-4"><Notice>{trackingIsOpen ? `Tracking closes ${formatDate(submission.trackingEndsAt)}. You can reject rule violations now, but verified views cannot be approved early.` : submission.trackingEndedForTestAt ? "Tracking was ended with an operator test override. Open the public link, confirm the posting account and final views, then approve or reject." : "Tracking is complete. Open the public link, confirm the posting account and final views, then approve or reject."}</Notice></div>
                  {submission.status === SUBMISSION_STATUSES.PENDING && !trackingIsOpen && submission.reviewDueAt && <div className="mt-3"><Notice tone={reviewIsOverdue ? "error" : "default"}>{reviewIsOverdue ? "This decision is overdue." : `Review requested by ${formatDate(submission.reviewDueAt)}.`}</Notice></div>}
                  {payoutIsLocked && <div className="mt-3"><Notice>This decision is locked because its payout is processing or has already transferred.</Notice></div>}
                  {submission.reviewReason && <div className="mt-4"><Notice>{submission.reviewReason}</Notice></div>}
                  {submission.dispute?.reason && <div className="mt-4"><Notice tone="error">Appeal from @{submission.clipperUsername}: {submission.dispute.reason}</Notice></div>}
                </div>
                <div>
                  <div className="grid gap-3 sm:grid-cols-2"><FormField label="Verified views"><input type="number" min="0" className={inputClass} value={inputs[submission.id]?.views ?? submission.approvedViews ?? ""} onChange={(event) => updateInput(submission.id, "views", event.target.value)} /></FormField><FormField label="Reason or note"><input className={inputClass} value={inputs[submission.id]?.reason || ""} onChange={(event) => updateInput(submission.id, "reason", event.target.value)} placeholder="Required when rejecting" /></FormField></div>
                  {Number(inputs[submission.id]?.views) > 0 && <div className="mt-3"><Notice>Gross {formatCurrency(calculateClipperPayout(Number(inputs[submission.id]?.views), campaign.rpmCents, submission.maxPayoutCents ?? campaign.maxPayoutPerSubmissionCents).grossAmountCents)} · 5% fee {formatCurrency(calculateClipperPayout(Number(inputs[submission.id]?.views), campaign.rpmCents, submission.maxPayoutCents ?? campaign.maxPayoutPerSubmissionCents).platformFeeCents)} · clipper earns {formatCurrency(calculateClipperPayout(Number(inputs[submission.id]?.views), campaign.rpmCents, submission.maxPayoutCents ?? campaign.maxPayoutPerSubmissionCents).amountCents)}</Notice></div>}
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <button disabled={!canDecide || workingId === submission.id || trackingIsOpen} onClick={() => handleReview(submission, "approve")} className="rounded-xl border border-emerald-300/25 bg-emerald-500/10 px-4 py-3 text-sm font-bold text-emerald-100 disabled:opacity-50">{submission.status === SUBMISSION_STATUSES.DISPUTED ? "Approve Appeal" : "Approve final views"}</button>
                    <button disabled={!canDecide || workingId === submission.id} onClick={() => handleReview(submission, "reject")} className="rounded-xl border border-red-300/25 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-100 disabled:opacity-50">{payoutIsLocked ? "Payout locked" : submission.status === SUBMISSION_STATUSES.DISPUTED ? "Uphold Rejection" : submission.status === SUBMISSION_STATUSES.APPROVED ? "Revoke unpaid payout" : "Reject with reason"}</button>
                  </div>
                  {hasCampaignOperations && trackingIsOpen && submission.status === SUBMISSION_STATUSES.PENDING && <button disabled={workingId === submission.id} onClick={() => handleEndTrackingForTest(submission)} className="mt-2 w-full rounded-xl border border-amber-300/25 bg-amber-500/10 px-4 py-3 text-sm font-bold text-amber-100 disabled:opacity-50">End Tracking for Test</button>}
                  {[SUBMISSION_STATUSES.APPROVED, SUBMISSION_STATUSES.REJECTED].includes(submission.status) && <div className="mt-5 border-t border-white/10 pt-5"><p className="mb-3 text-sm font-semibold text-white">Review @{submission.clipperUsername}</p><PublicReviewForm campaignId={campaignId} currentUser={currentUser} subjectId={submission.clipperId} /></div>}
                </div>
              </div>
            </Card>
            );
          })}
        </div>
        )}
      </Section>
    </PageShell>
  );
}

export function CampaignPayoutsPage() {
  const { currentUser } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  useEffect(() => subscribePayouts({ userId: currentUser?.uid, scope: "clipper", onData: setPayouts, onError: (loadError) => setError(loadError.message) }), [currentUser?.uid]);
  const availableStatuses = new Set([PAYOUT_STATUSES.AVAILABLE, PAYOUT_STATUSES.FAILED]);
  const availablePayouts = payouts.filter((item) => availableStatuses.has(item.status));
  const availableTotal = availablePayouts.reduce((sum, item) => sum + (item.amountCents || 0), 0);
  const pendingWithdrawalId = payouts.find((item) => item.status === PAYOUT_STATUSES.PROCESSING && item.withdrawalId)?.withdrawalId;
  const pendingPayouts = pendingWithdrawalId
    ? payouts.filter((item) => item.status === PAYOUT_STATUSES.PROCESSING && item.withdrawalId === pendingWithdrawalId)
    : [];
  const selectedPayouts = pendingPayouts.length > 0 ? pendingPayouts : availablePayouts;
  const selectedTotal = selectedPayouts.reduce((sum, item) => sum + (item.amountCents || 0), 0);
  const selectedPayoutIds = selectedPayouts.map((item) => item.id);
  const paidTotal = payouts.filter((item) => item.status === PAYOUT_STATUSES.PAID).reduce((sum, item) => sum + (item.amountCents || 0), 0);
  const verifiedViews = payouts.reduce((sum, item) => sum + (item.approvedViews || 0), 0);

  const openOnboarding = (result) => {
    if (result.onboardingUrl) {
      assignSafeExternalUrl(result.onboardingUrl);
      return true;
    }
    return false;
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError("");
      setNotice("");
      const result = await startCampaignPayoutOnboarding({ user: currentUser });
      if (!openOnboarding(result)) setNotice("Your Stripe payout account is ready.");
    } catch (connectError) {
      setError(connectError.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleWithdraw = async () => {
    try {
      setWithdrawing(true);
      setError("");
      setNotice("");
      const result = await withdrawCampaignBalance({ payoutIds: selectedPayoutIds, user: currentUser });
      if (!openOnboarding(result)) {
        setNotice(result.idempotent ? "These earnings were already transferred." : `${formatCurrency(result.amountCents)} was transferred to your Stripe account.`);
      }
    } catch (withdrawError) {
      setError(withdrawError.message);
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <PageShell
      title="Clipping Campaign Earnings"
      description="Private RPM earnings and Stripe payouts for approved clipping campaign submissions."
      canonicalPath="/clipping-campaigns/payouts"
    >
      <Hero
        eyebrow="Campaign earnings"
        title="Withdraw approved campaign earnings through Stripe"
        copy="Creators verify views and approve earnings at the campaign RPM. Stripe handles your connected payout account and bank deposits."
      >
        {currentUser && <button type="button" onClick={handleConnect} disabled={connecting} className="rounded-full px-7 py-3.5 text-sm font-semibold text-black transition disabled:opacity-50" style={{ backgroundColor: NEON }}>{connecting ? "Opening Stripe" : "Connect Stripe"}</button>}
        <CTAButton to="/clipping-campaigns/dashboard">Back to Dashboard</CTAButton>
      </Hero>
      <Section className="!pt-0">
        {!currentUser ? <EmptyState title="Sign in to view campaign earnings" copy="Campaign earnings are private and tied to your Kre8 account." action={<CTAButton to={withNextPath("/login", "/clipping-campaigns/payouts")}>Sign In</CTAButton>} /> : <>
        {error && <div className="mb-5"><Notice tone="error">{error}</Notice></div>}
        {notice && <div className="mb-5"><Notice tone="success">{notice}</Notice></div>}
        <div className="mb-6"><Notice>Approved earnings shown here are after the 5% clipper service fee. Withdraw your combined available balance once it reaches {formatCurrency(MINIMUM_WITHDRAWAL_CENTS)}.</Notice></div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Metric label="Available" value={formatCurrency(availableTotal)} icon={BadgeDollarSign} />
          <Metric label="Transferred" value={formatCurrency(paidTotal)} icon={Wallet} />
          <Metric label="Verified views" value={formatViews(verifiedViews)} icon={Eye} tone="purple" />
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-5"><div><p className="font-semibold text-white">{pendingPayouts.length > 0 ? "Pending transfer" : "Available balance"} {formatCurrency(selectedTotal)}</p><p className="mt-1 text-sm text-zinc-500">{pendingPayouts.length > 0 ? "Resume this exact transfer safely using its original Stripe request." : availableTotal < MINIMUM_WITHDRAWAL_CENTS ? `${formatCurrency(MINIMUM_WITHDRAWAL_CENTS - availableTotal)} more needed to withdraw` : `${selectedPayoutIds.length} earning${selectedPayoutIds.length === 1 ? "" : "s"} ready`}</p></div><button type="button" onClick={handleWithdraw} disabled={withdrawing || selectedPayoutIds.length === 0 || (pendingPayouts.length === 0 && availableTotal < MINIMUM_WITHDRAWAL_CENTS)} className="rounded-xl px-5 py-3 text-sm font-bold text-black disabled:opacity-40" style={{ backgroundColor: NEON }}>{withdrawing ? "Transferring Balance" : pendingPayouts.length > 0 ? "Resume Pending Transfer" : "Withdraw Available Balance"}</button></div>
        <div className="mt-8 space-y-3">{payouts.length === 0 ? <EmptyState title="No campaign earnings yet" copy="Approved campaign submissions will appear here after their tracking windows close." /> : payouts.map((payout) => <Card key={payout.id}><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[1.5fr_0.7fr_0.8fr_0.8fr] lg:items-center"><div><p className="font-semibold text-white">{payout.campaignTitle}</p><a href={getSafeExternalUrl(payout.clipUrl) || undefined} target="_blank" rel="noopener noreferrer" className="mt-1 block truncate text-xs" style={{ color: NEON }}>{payout.platform} clip</a></div><div><p className="text-xs uppercase tracking-wider text-zinc-500">Views</p><p className="mt-1 text-sm text-white">{formatViews(payout.approvedViews)}</p></div><div><p className="text-xs uppercase tracking-wider text-zinc-500">Earning</p><p className="mt-1 text-sm font-semibold text-white">{formatCurrency(payout.amountCents)}</p><p className="mt-1 text-xs text-zinc-500">{formatCurrency(payout.grossAmountCents || payout.amountCents)} gross · {formatCurrency(payout.platformFeeCents || 0)} fee</p></div><div><p className="text-xs uppercase tracking-wider text-zinc-500">Status</p><div className="mt-1"><Badge tone={payout.status === PAYOUT_STATUSES.FAILED ? "warn" : payout.status === PAYOUT_STATUSES.PAID ? "live" : "purple"}>{humanizeStatus(payout.status)}</Badge></div></div></div></Card>)}</div>
        </>}
      </Section>
    </PageShell>
  );
}

export function CampaignAdminPage() {
  const { currentUser, activeUser, authLoading } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [verificationReasons, setVerificationReasons] = useState({});
  const [verificationWorkingId, setVerificationWorkingId] = useState("");
  const [operationsQueue, setOperationsQueue] = useState({ disputes: [], overdueReviews: [] });
  const [workingId, setWorkingId] = useState("");
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const hasCampaignOperations = activeUser?.profileInfo?.campaignOperations === true || activeUser?.profileInfo?.isAdmin === true;

  useEffect(() => {
    if (!currentUser || !hasCampaignOperations) return undefined;
    return subscribeAllCampaigns(currentUser.uid, setCampaigns, (loadError) => setError(loadError.message));
  }, [currentUser, hasCampaignOperations]);

  const loadPendingAccounts = async () => {
    if (!currentUser || !hasCampaignOperations) return;
    try {
      setPendingAccounts(await listSocialAccounts({ user: currentUser, scope: "pending" }));
    } catch (loadError) {
      setError(loadError.message);
    }
  };
  useEffect(() => { loadPendingAccounts(); }, [currentUser, hasCampaignOperations]);

  useEffect(() => {
    if (!currentUser || !hasCampaignOperations) return;
    listCampaignOperationsQueue({ user: currentUser })
      .then((queue) => setOperationsQueue({
        disputes: queue.disputes || [],
        overdueReviews: queue.overdueReviews || [],
      }))
      .catch((loadError) => setError(loadError.message));
  }, [currentUser, hasCampaignOperations]);

  const handleVerification = async (account, action) => {
    try {
      setVerificationWorkingId(account.id);
      setError("");
      await reviewSocialAccountVerification({
        user: currentUser,
        accountId: account.id,
        action,
        reason: verificationReasons[account.id] || "",
      });
      await loadPendingAccounts();
    } catch (reviewError) {
      setError(reviewError.message);
    } finally {
      setVerificationWorkingId("");
    }
  };

  const handleStatus = async (campaignId, status) => {
    try {
      setWorkingId(campaignId);
      setError("");
      await updateCampaignStatus({ campaignId, user: currentUser, status });
    } catch (statusError) {
      setError(statusError.message);
    } finally {
      setWorkingId("");
    }
  };

  const handleRefund = async (campaignId) => {
    try {
      setWorkingId(campaignId);
      setError("");
      await refundUnusedCampaignBudget({ campaignId, user: currentUser });
    } catch (refundError) {
      setError(refundError.message);
    } finally {
      setWorkingId("");
    }
  };

  const attentionCount = campaigns.filter((campaign) => campaign.fundingStatus === "paid_needs_attention").length;
  const liveCount = campaigns.filter((campaign) => campaign.status === CAMPAIGN_STATUSES.LIVE).length;
  const refundableCents = campaigns.reduce((sum, campaign) => {
    const closed = [CAMPAIGN_STATUSES.CLOSED, CAMPAIGN_STATUSES.COMPLETED].includes(campaign.status);
    return sum + (closed && campaign.fundingStatus === "funded" ? campaign.budgetRemainingCents || 0 : 0);
  }, 0);
  const visibleCampaigns = campaigns.filter((campaign) => {
    if (filter === "attention") return campaign.fundingStatus === "paid_needs_attention";
    if (filter === "live") return campaign.status === CAMPAIGN_STATUSES.LIVE;
    if (filter === "draft") return campaign.status === CAMPAIGN_STATUSES.DRAFT;
    if (filter === "closed") return [CAMPAIGN_STATUSES.CLOSED, CAMPAIGN_STATUSES.COMPLETED].includes(campaign.status);
    return true;
  });

  return (
    <PageShell title="Campaign Operations" description="Private campaign funding, exception, status, and refund controls." canonicalPath="/clipping-campaigns/admin">
      <Hero eyebrow="Campaign operations" title="Watch funding and handle the exceptions" copy="Paid, complete drafts launch automatically. Use this workspace for payment issues, campaign status changes, and unused budget refunds." />
      <Section className="!pt-0">
        {authLoading ? <LoadingState label="Checking campaign operations access" /> : !currentUser ? <EmptyState title="Sign in to open campaign operations" copy="Campaign operations require a permitted account." action={<CTAButton to={withNextPath("/login", "/clipping-campaigns/admin")}>Sign In</CTAButton>} /> : !hasCampaignOperations ? <EmptyState title="Kre8 operations access required" copy="Campaign owners review member account checks from their campaign submission workspace. This page contains platform-wide controls reserved for Kre8 operators." action={<CTAButton to="/clipping-campaigns/dashboard">Open Campaign Dashboard</CTAButton>} /> : <>
          {error && <div className="mb-5"><Notice tone="error">{error}</Notice></div>}
          <Card className="mb-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div><p className="text-xs font-semibold uppercase tracking-wider" style={{ color: NEON }}>Action queue</p><h2 className="mt-2 text-2xl font-bold text-white">Disputes and overdue reviews</h2></div>
              <div className="flex gap-2"><Badge tone="warn">{operationsQueue.disputes.length} disputes</Badge><Badge tone="purple">{operationsQueue.overdueReviews.length} overdue</Badge></div>
            </div>
            <div className="mt-5 divide-y divide-white/10">
              {operationsQueue.disputes.length === 0 && operationsQueue.overdueReviews.length === 0 ? <Notice>No disputes or overdue campaign reviews need attention.</Notice> : <>
                {operationsQueue.disputes.map((submission) => <div key={`dispute-${submission.id}`} className="flex flex-wrap items-center justify-between gap-3 py-4 first:pt-0"><div><p className="font-semibold text-white">Appeal from @{submission.clipperUsername}</p><p className="mt-1 text-sm text-zinc-500">{submission.campaignTitle} · {submission.platform}</p></div><CTAButton to={`/clipping-campaigns/${submission.campaignId}/review`} variant="secondary">Resolve Appeal</CTAButton></div>)}
                {operationsQueue.overdueReviews.map((submission) => <div key={`overdue-${submission.id}`} className="flex flex-wrap items-center justify-between gap-3 py-4 first:pt-0"><div><p className="font-semibold text-white">Overdue review for @{submission.clipperUsername}</p><p className="mt-1 text-sm text-zinc-500">{submission.campaignTitle} · due {formatDate(submission.reviewDueAt)}</p></div><CTAButton to={`/clipping-campaigns/${submission.campaignId}/review`} variant="secondary">Open Review</CTAButton></div>)}
              </>}
            </div>
          </Card>
          <Card className="mb-6">
            <div className="flex flex-wrap items-end justify-between gap-3"><div><p className="text-xs font-semibold uppercase tracking-wider" style={{ color: NEON }}>Social verification</p><h2 className="mt-2 text-2xl font-bold text-white">Pending bio checks</h2></div><Badge tone="purple">{pendingAccounts.length} pending</Badge></div>
            <div className="mt-5 space-y-3">{pendingAccounts.length === 0 ? <Notice>No social accounts are waiting for verification.</Notice> : pendingAccounts.map((account) => <div key={account.id} className="grid gap-4 border-t border-white/10 pt-4 first:border-0 first:pt-0 lg:grid-cols-[1fr_1fr_auto] lg:items-end"><div><p className="font-semibold text-white">{account.platform} · @{account.handle}</p><a href={getSafeExternalUrl(account.profileUrl) || undefined} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-2 text-sm" style={{ color: NEON }}>Open profile <ArrowRight className="h-4 w-4" /></a><p className="mt-2 text-xs text-zinc-500">Confirm this bio contains <strong className="text-white">{account.verificationCode}</strong></p></div><FormField label="Rejection reason"><input className={inputClass} value={verificationReasons[account.id] || ""} onChange={(event) => setVerificationReasons((previous) => ({ ...previous, [account.id]: event.target.value }))} placeholder="Only required when rejecting" /></FormField><div className="flex gap-2"><button type="button" disabled={verificationWorkingId === account.id} onClick={() => handleVerification(account, "approve")} className="rounded-xl border border-emerald-300/25 bg-emerald-500/10 px-4 py-3 text-sm font-bold text-emerald-100 disabled:opacity-50">Approve</button><button type="button" disabled={verificationWorkingId === account.id} onClick={() => handleVerification(account, "reject")} className="rounded-xl border border-red-300/25 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-100 disabled:opacity-50">Reject</button></div></div>)}</div>
          </Card>
          <div className="grid gap-4 sm:grid-cols-3"><Metric label="Live campaigns" value={liveCount} icon={Scissors} /><Metric label="Payment issues" value={attentionCount} icon={ClipboardList} tone="purple" /><Metric label="Refundable budget" value={formatCurrency(refundableCents)} icon={Wallet} /></div>
          <div className="my-6 flex flex-wrap gap-2">{[["all", "All"], ["attention", "Payment issues"], ["live", "Live"], ["draft", "Drafts"], ["closed", "Closed"]].map(([value, label]) => <button key={value} type="button" onClick={() => setFilter(value)} className={`rounded-lg border border-white/10 px-4 py-2 text-sm font-semibold transition ${filter === value ? "bg-white/10 text-white" : "bg-white/[0.03] text-zinc-400"}`}>{label}</button>)}</div>
          <div className="space-y-4">{visibleCampaigns.length === 0 ? <EmptyState title="No campaigns in this view" copy="Campaigns will appear here as creators save, fund, launch, and close them." /> : visibleCampaigns.map((campaign) => {
            const canRefund = [CAMPAIGN_STATUSES.CLOSED, CAMPAIGN_STATUSES.COMPLETED].includes(campaign.status) && campaign.fundingStatus === "funded" && (campaign.budgetRemainingCents || 0) > 0;
            return <Card key={campaign.id}><div className="grid gap-5 lg:grid-cols-[1fr_220px] lg:items-center"><div><div className="flex flex-wrap gap-2"><Badge tone={campaign.status === CAMPAIGN_STATUSES.LIVE ? "live" : "neutral"}>{humanizeStatus(campaign.status)}</Badge><FundingBadge campaign={campaign} /><Badge tone="purple">{campaign.category}</Badge></div><h2 className="mt-3 text-xl font-bold text-white">{campaign.title}</h2><p className="mt-1 text-sm text-zinc-400">@{campaign.creatorUsername} · {formatCurrency(campaign.budgetCents)} budget · {formatCurrency(campaign.budgetRemainingCents)} remaining · {formatCurrency(campaign.rpmCents)} RPM</p>{campaign.fundingStatus === "paid_needs_attention" && <p className="mt-2 text-sm text-amber-200">Payment succeeded, but the paid campaign details did not match the checkout session. Review Stripe and the campaign record before changing its status.</p>}<div className="mt-3 flex flex-wrap gap-4"><Link to={`/clipping-campaigns/${campaign.id}`} className="text-sm font-semibold" style={{ color: NEON }}>Open campaign details</Link><Link to={`/clipping-campaigns/${campaign.id}/review`} className="text-sm font-semibold text-white">Test submissions</Link>{canRefund && <button type="button" disabled={workingId === campaign.id} onClick={() => handleRefund(campaign.id)} className="text-sm font-semibold text-white disabled:opacity-50">Refund {formatCurrency(campaign.budgetRemainingCents)} unused budget</button>}</div></div><FormField label="Campaign status"><select disabled={workingId === campaign.id} className={inputClass} value={campaign.status} onChange={(event) => handleStatus(campaign.id, event.target.value)}>{Object.values(CAMPAIGN_STATUSES).map((status) => <option key={status} value={status}>{humanizeStatus(status)}</option>)}</select></FormField></div></Card>;
          })}</div>
        </>}
      </Section>
    </PageShell>
  );
}
